/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faUsers,
  faBuilding,
  faCalendar,
  faChartBar,
  faCog,
  faCheckCircle,
  faTrophy,
  faArrowUp,
  faArrowDown,
  faUser,
  faUpload,
  faMapMarkerAlt,
  faGraduationCap,
  faBriefcase,
  faClipboardCheck,
  faUserTie,
  faBookOpen,
  faVideo,
  faFileAlt,
  faNetworkWired,
  faSearch,
  faCalendarCheck,
  faClipboardList,
  faBrain,
  faTags,
  faComments,
  faLightbulb,
  faFilePdf,
  faDownload,
  faHandshake,
  faUsersCog,
  faFileExport,
  faFilter,
  faSpinner,
  faEdit,
  faTrash,
  faPlus,
  faUserCheck,
  faUserPlus,
  faX,
  faStar,
  faAward
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import UniversityDashboardHeader from '../components/UniversityDashboardHeader';
import StatCard from '../components/StatCard';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import '../styles/UniversityDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MoringaDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentGenderFilter, setStudentGenderFilter] = useState('all'); // 'all', 'male', 'female'
  const [studentDeptFilter, setStudentDeptFilter] = useState('all');
  
  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  
  // Dashboard stats
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalStudents: 0,
      activeCompanies: 0,
      upcomingEvents: 0,
      placementRate: 0,
      studentsPlaced: 0,
      activeJobSeekers: 0,
      averageSalary: 0,
      activeInterns: 0,
      coachingSessions: 0,
      completedAssessments: 0,
      mockInterviews: 0,
      jobApplications: 0
    },
    profileCompletion: 0,
    departments: [],
    recentPlacements: []
  });

  // Career Coaching state
  const [coachingModules, setCoachingModules] = useState([]);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [coachingSessions, setCoachingSessions] = useState([]);
  const [showCoachingForm, setShowCoachingForm] = useState(false);
  const [editingCoachingSession, setEditingCoachingSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [coachingForm, setCoachingForm] = useState({
    studentId: '',
    coachId: '',
    date: '',
    time: '',
    hour: '12',
    minute: '00',
    ampm: 'PM',
    type: 'Career Planning',
    notes: ''
  });

  // Job Applications Tracking state
  const [jobApplications, setJobApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationFilters, setApplicationFilters] = useState({
    status: '',
    search: '' // Combined search for student name, company, and position
  });

  // Interview Progress Tracking state
  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(false);
  const [interviewFilters, setInterviewFilters] = useState({
    status: '',
    student: '',
    dateRange: ''
  });

  // Aptitude Assessment state
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [assessmentStudents, setAssessmentStudents] = useState([]);
  const [assessmentResults, setAssessmentResults] = useState([]);
  const [assessmentForm, setAssessmentForm] = useState({
    title: '',
    description: '',
    type: 'Technical',
    duration: '30 min',
    questions: []
  });
  const [questionPdfFile, setQuestionPdfFile] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);

  // Graduate Categorisation state
  const [graduates, setGraduates] = useState([]);
  const [graduatesLoading, setGraduatesLoading] = useState(false);
  const [categoryFilters, setCategoryFilters] = useState({
    category: '',
    department: '',
    year: '',
    skillLevel: '',
    experience: ''
  });

  // Mock Interview Resources state
  const [mockInterviewResources, setMockInterviewResources] = useState([]);
  const [mockInterviews, setMockInterviews] = useState([]);
  const [mockInterviewLoading, setMockInterviewLoading] = useState(false);
  const [interviewResourceFilter, setInterviewResourceFilter] = useState('all'); // Filter by department
  const [showMockInterviewForm, setShowMockInterviewForm] = useState(false);
  const [mockInterviewForm, setMockInterviewForm] = useState({
    studentId: '',
    studentName: '',
    type: 'Technical',
    date: '',
    hour: '12',
    minute: '00',
    ampm: 'PM',
    duration: '30',
    notes: ''
  });

  // Job Board Integration state
  const [jobBoards, setJobBoards] = useState([]);
  const [jobBoardLoading, setJobBoardLoading] = useState(false);
  const [aksharJobs, setAksharJobs] = useState([]);
  const [jobCategoryFilter, setJobCategoryFilter] = useState('all');

  // CV Booklets state
  const [cvBooklets, setCvBooklets] = useState([]);
  const [cvBookletsLoading, setCvBookletsLoading] = useState(false);
  const [showCvBookletForm, setShowCvBookletForm] = useState(false);
  const [cvBookletForm, setCvBookletForm] = useState({
    cohort: '',
    skillSet: '',
    year: '',
    department: '',
    format: 'PDF',
    includePhoto: true,
    includeContact: true,
    photoUploads: [], // Array of {studentId, photoFile}
    contactInfo: {
      includeEmail: true,
      includePhone: true,
      includeAddress: false,
      includeLinkedIn: false,
      includePortfolio: false
    }
  });
  const [showSamplePreview, setShowSamplePreview] = useState(false);
  const [generatingBooklet, setGeneratingBooklet] = useState(false);
  const [bookletMode, setBookletMode] = useState('generate'); // 'generate' or 'upload'
  const [uploadedBookletFile, setUploadedBookletFile] = useState(null);

  // Employer Partner Events state
  const [employerEvents, setEmployerEvents] = useState([]);
  const [employerEventsLoading, setEmployerEventsLoading] = useState(false);
  const [showEmployerEventForm, setShowEmployerEventForm] = useState(false);
  const [employerEventForm, setEmployerEventForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    partnerCompany: '',
    eventType: 'Networking',
    maxAttendees: '',
    contactEmail: '',
    contactPhone: ''
  });
  const [editingEmployerEvent, setEditingEmployerEvent] = useState(null);
  const [myRegisteredEvents, setMyRegisteredEvents] = useState([]);
  const [showEventRegistrations, setShowEventRegistrations] = useState(false);
  const [selectedEventRegistrations, setSelectedEventRegistrations] = useState(null);
  const [eventRegistrationsLoading, setEventRegistrationsLoading] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [markingAttendance, setMarkingAttendance] = useState(false);

  // Alumni Success Stories state
  const [alumniStories, setAlumniStories] = useState([]);
  const [alumniStoriesLoading, setAlumniStoriesLoading] = useState(false);
  const [showAlumniStoryForm, setShowAlumniStoryForm] = useState(false);
  const [alumniStoryForm, setAlumniStoryForm] = useState({
    alumniName: '',
    graduationYear: '',
    department: '',
    currentPosition: '',
    currentCompany: '',
    location: '',
    salaryRange: '',
    careerTrajectory: [],
    testimonial: '',
    achievements: [],
    photoUrl: '',
    linkedinUrl: '',
    email: '',
    isPublished: true
  });
  const [editingAlumniStory, setEditingAlumniStory] = useState(null);

  // First Destination Survey state
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [surveyStatistics, setSurveyStatistics] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [showSurveyForm, setShowSurveyForm] = useState(false);
  const [showSurveyStatistics, setShowSurveyStatistics] = useState(false);
  const [surveyForm, setSurveyForm] = useState({
    studentName: '',
    studentEmail: '',
    graduationYear: '',
    graduationDate: '',
    department: '',
    degree: '',
    destination: '',
    destinationDetails: '',
    employmentStatus: '',
    jobTitle: '',
    companyName: '',
    industry: '',
    location: '',
    salaryRange: '',
    startDate: '',
    jobSearchDuration: '',
    educationType: '',
    institutionName: '',
    programName: '',
    programStartDate: '',
    businessName: '',
    businessType: '',
    businessSector: '',
    isJobRelatedToDegree: null,
    satisfactionLevel: '',
    additionalComments: ''
  });

  // Navigation items for Moringa Career Services dashboard
  const navigationItems = useMemo(() => {
    const totalStudents = dashboardData.stats.totalStudents || 0;
    return [
      { id: 'dashboard', icon: faChartLine, label: 'Dashboard', section: 'dashboard' },
      { 
        id: 'career-coaching', 
        icon: faUserTie, 
        label: 'Career Coaching', 
        section: 'career-coaching'
      },
      { 
        id: 'job-applications', 
        icon: faBriefcase, 
        label: 'Job Applications', 
        section: 'job-applications',
        badge: dashboardData.stats.jobApplications > 0 ? String(dashboardData.stats.jobApplications) : undefined
      },
      { 
        id: 'interview-tracking', 
        icon: faCalendarCheck, 
        label: 'Interview Progress', 
        section: 'interview-tracking'
      },
      { 
        id: 'aptitude-assessment', 
        icon: faBrain, 
        label: 'Aptitude Assessment', 
        section: 'aptitude-assessment'
      },
      { 
        id: 'graduate-categorisation', 
        icon: faTags, 
        label: 'Graduate Categorisation', 
        section: 'graduate-categorisation'
      },
      { 
        id: 'mock-interviews', 
        icon: faVideo, 
        label: 'Mock Interviews', 
        section: 'mock-interviews'
      },
      { 
        id: 'job-boards', 
        icon: faNetworkWired, 
        label: 'Job Board Integration', 
        section: 'job-boards'
      },
      { 
        id: 'cv-booklets', 
        icon: faFilePdf, 
        label: 'CV Booklets', 
        section: 'cv-booklets'
      },
      { 
        id: 'employer-events', 
        icon: faHandshake, 
        label: 'Employer Events', 
        section: 'employer-events'
      },
      { 
        id: 'alumni-stories', 
        icon: faStar, 
        label: 'Alumni Success Stories', 
        section: 'alumni-stories'
      },
      { 
        id: 'first-destination-survey', 
        icon: faClipboardList, 
        label: 'First Destination Survey', 
        section: 'first-destination-survey'
      },
      { id: 'students', icon: faUsers, label: 'Students', section: 'students', badge: totalStudents > 0 ? String(totalStudents) : undefined },
      { id: 'analytics', icon: faChartBar, label: 'Analytics', section: 'analytics' },
      { id: 'settings', icon: faCog, label: 'Settings', section: 'settings' },
      { id: 'myprofile', icon: faUser, label: 'My Profile', section: 'myprofile' }
    ];
  }, [dashboardData.stats.totalStudents, dashboardData.stats.jobApplications]);

  // Handle navigation click
  const handleNavClick = (item) => {
    if (item.section === 'myprofile') {
      navigate('/university-my-profile');
      return;
    }
    setActiveSection(item.section);
  };

  // Get section title for header
  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'career-coaching': 'Career Coaching & Prep',
      'job-applications': 'Job Applications Tracking',
      'interview-tracking': 'Interview Progress Tracking',
      'aptitude-assessment': 'Aptitude Assessment',
      'graduate-categorisation': 'Graduate Categorisation',
      'mock-interviews': 'Mock Interview Prep',
      'job-boards': 'Job Board Integration',
      'cv-booklets': 'CV Booklets Generator',
      'employer-events': 'Employer Partner Events',
      'alumni-stories': 'Alumni Success Stories',
      'first-destination-survey': 'First Destination Survey',
      'students': 'Students',
      'analytics': 'Analytics',
      'settings': 'Settings'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch stats
      const statsResponse = await axios.get(
        buildApiUrl('/api/university/stats'),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Fetch profile to calculate completion
      let profileCompletion = 0;
      try {
        const profileResponse = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (profileResponse.data) {
          const profile = profileResponse.data;
          const requiredFields = [
            'universityName',
            'institutionType',
            'registrationNumber',
            'officialEmail',
            'phoneNumber',
            'physicalAddress',
            'city',
            'county',
            'contactPersonName',
            'contactPersonTitle',
            'contactPersonPhone',
            'contactPersonEmail'
          ];
          
          const filledFields = requiredFields.filter(field => {
            const value = profile[field];
            return value && value.toString().trim() !== '';
          }).length;
          
          const optionalFields = [
            'websiteUrl',
            'totalStudentEnrollment',
            'numberOfPrograms',
            'careerCenterEmail',
            'careerOfficePhone',
            'areasOfSpecialization'
          ];
          
          const filledOptional = optionalFields.filter(field => {
            const value = profile[field];
            if (field === 'areasOfSpecialization') {
              return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
            }
            return value && value.toString().trim() !== '';
          }).length;
          
          const requiredPercentage = (filledFields / requiredFields.length) * 70;
          const optionalPercentage = (filledOptional / optionalFields.length) * 30;
          profileCompletion = Math.round(requiredPercentage + optionalPercentage);
          
          // Use the actual calculated percentage - don't override with flags
          // The profile.profileCompleted flag may be incorrectly set, so we trust the calculation
          console.log(`📊 Moringa Profile Completion Calculation:`, {
            filledRequired: `${filledFields}/${requiredFields.length}`,
            filledOptional: `${filledOptional}/${optionalFields.length}`,
            calculatedPercentage: `${profileCompletion}%`,
            profileCompletedFlag: profile.profileCompleted || false,
            hasCompletedProfileFlag: profile.hasCompletedProfile || false,
            note: 'Using calculated percentage, not flag-based override'
          });
        }
      } catch (profileError) {
        console.error('Error fetching profile for completion:', profileError);
      }

      if (statsResponse.data?.success) {
        const stats = statsResponse.data.stats || {};
        const departments = statsResponse.data.departments || [];

        setDashboardData(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            totalStudents: stats.totalStudents || 0,
            activeCompanies: stats.companiesRecruiting || 0,
            upcomingEvents: stats.upcomingEvents || 0,
            placementRate: stats.placementRate || 0,
            studentsPlaced: stats.studentsPlaced || 0,
            activeJobSeekers: stats.activeJobSeekers || 0,
            averageSalary: stats.averageSalary || 0,
            activeInterns: stats.activeInterns || 0,
            coachingSessions: stats.coachingSessions || 0,
            completedAssessments: stats.completedAssessments || 0,
            mockInterviews: stats.mockInterviews || 0,
            jobApplications: stats.jobApplications || 0
          },
          departments,
          profileCompletion: Math.max(0, Math.min(100, profileCompletion))
        }));
      }

      // Fetch Moringa-specific stats
      try {
        const moringaStatsResponse = await axios.get(
          buildApiUrl('/api/moringa/job-applications'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (moringaStatsResponse.data?.success) {
          setDashboardData(prev => ({
            ...prev,
            stats: {
              ...prev.stats,
              jobApplications: moringaStatsResponse.data.total || 0
            }
          }));
        }
        
        // Fetch coaching sessions count
        const coachingResponse = await axios.get(
          buildApiUrl('/api/moringa/career-coaching/modules'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (coachingResponse.data?.success) {
          setDashboardData(prev => ({
            ...prev,
            stats: {
              ...prev.stats,
              coachingSessions: coachingResponse.data.sessions?.length || 0
            }
          }));
        }
        
        // Fetch mock interviews count
        const mockInterviewsResponse = await axios.get(
          buildApiUrl('/api/moringa/mock-interviews'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (mockInterviewsResponse.data?.success) {
          setDashboardData(prev => ({
            ...prev,
            stats: {
              ...prev.stats,
              mockInterviews: mockInterviewsResponse.data.sessions?.length || 0
            }
          }));
        }
        
        // Fetch assessments count
        const assessmentsResponse = await axios.get(
          buildApiUrl('/api/moringa/assessments'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (assessmentsResponse.data?.success) {
          const totalCompleted = assessmentsResponse.data.assessments?.reduce((sum, a) => sum + (a.completedCount || 0), 0) || 0;
          setDashboardData(prev => ({
            ...prev,
            stats: {
              ...prev.stats,
              completedAssessments: totalCompleted
            }
          }));
        }
      } catch (error) {
        console.error('Error fetching Moringa-specific stats:', error);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch students list for forms
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/moringa/students'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          setStudents(response.data.students || []);
        }
      } catch (error) {
        console.error('Failed to fetch students:', error);
      }
    };
    
    fetchStudents();
  }, []);

  // Fetch career coaching modules
  useEffect(() => {
    const fetchCoachingModules = async () => {
      if (activeSection !== 'career-coaching') return;

      try {
        setCoachingLoading(true);
        const token = localStorage.getItem('token');
        // This would be a new endpoint: /api/moringa/career-coaching/modules
        const response = await axios.get(
          buildApiUrl('/api/moringa/career-coaching/modules'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setCoachingModules(response.data.modules || []);
          setCoachingSessions(response.data.sessions || []);
        }
      } catch (error) {
        console.error('Failed to fetch coaching modules:', error);
        // Set default modules if API doesn't exist yet
        setCoachingModules([
          { id: 1, title: 'Career Planning Fundamentals', description: 'Learn the basics of career planning', status: 'available', duration: '2 hours' },
          { id: 2, title: 'Resume & Cover Letter Writing', description: 'Master the art of writing compelling resumes', status: 'available', duration: '3 hours' },
          { id: 3, title: 'Interview Preparation', description: 'Prepare for success in job interviews', status: 'available', duration: '2.5 hours' },
          { id: 4, title: 'Networking Strategies', description: 'Build your professional network effectively', status: 'available', duration: '2 hours' },
          { id: 5, title: 'Salary Negotiation', description: 'Learn how to negotiate your salary', status: 'available', duration: '1.5 hours' }
        ]);
      } finally {
        setCoachingLoading(false);
      }
    };

    fetchCoachingModules();
  }, [activeSection]);

  // Fetch job applications
  useEffect(() => {
    const fetchJobApplications = async () => {
      if (activeSection !== 'job-applications') return;

      try {
        setApplicationsLoading(true);
        const token = localStorage.getItem('token');
        // This would be a new endpoint: /api/moringa/job-applications
        const response = await axios.get(
          buildApiUrl('/api/moringa/job-applications'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setJobApplications(response.data.applications || []);
        }
      } catch (error) {
        console.error('Failed to fetch job applications:', error);
        setJobApplications([]);
      } finally {
        setApplicationsLoading(false);
      }
    };

    fetchJobApplications();
  }, [activeSection]);

  // Fetch interview progress
  useEffect(() => {
    const fetchInterviews = async () => {
      if (activeSection !== 'interview-tracking') return;

      try {
        setInterviewsLoading(true);
        const token = localStorage.getItem('token');
        // This would be a new endpoint: /api/moringa/interviews
        const response = await axios.get(
          buildApiUrl('/api/moringa/interviews'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setInterviews(response.data.interviews || []);
        }
      } catch (error) {
        console.error('Failed to fetch interviews:', error);
        setInterviews([]);
      } finally {
        setInterviewsLoading(false);
      }
    };

    fetchInterviews();
  }, [activeSection]);

  // Fetch aptitude assessments
  useEffect(() => {
    const fetchAssessments = async () => {
      if (activeSection !== 'aptitude-assessment') return;

      try {
        setAssessmentsLoading(true);
        const token = localStorage.getItem('token');
        // This would be a new endpoint: /api/moringa/assessments
        const response = await axios.get(
          buildApiUrl('/api/moringa/assessments'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setAssessments(response.data.assessments || []);
        }
      } catch (error) {
        console.error('Failed to fetch assessments:', error);
        setAssessments([]);
      } finally {
        setAssessmentsLoading(false);
      }
    };

    fetchAssessments();
  }, [activeSection]);

  // Fetch students for assessment assignment (cached to avoid refetching)
  const fetchAssessmentStudents = async () => {
    // Only fetch if we don't have students cached already
    if (assessmentStudents.length > 0) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        buildApiUrl('/api/moringa/assessments/students'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setAssessmentStudents(response.data.students || []);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setAssessmentStudents([]);
    }
  };

  // Fetch assessment results
  const fetchAssessmentResults = async (assessmentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        buildApiUrl(`/api/moringa/assessments/${assessmentId}/results`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setAssessmentResults(response.data.results || []);
      }
    } catch (error) {
      console.error('Failed to fetch assessment results:', error);
      setAssessmentResults([]);
    }
  };

  // Handle create assessment
  const handleCreateAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Use FormData if PDF is uploaded, otherwise use JSON
      let requestData;
      let headers;
      
      if (questionPdfFile) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('title', assessmentForm.title);
        formData.append('description', assessmentForm.description);
        formData.append('type', assessmentForm.type);
        formData.append('duration', assessmentForm.duration);
        formData.append('questions', JSON.stringify(assessmentForm.questions));
        formData.append('questionPdf', questionPdfFile);
        
        requestData = formData;
        headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        };
      } else {
        // Use JSON for regular data
        requestData = assessmentForm;
        headers = { Authorization: `Bearer ${token}` };
      }
      
      const response = await axios.post(
        buildApiUrl('/api/moringa/assessments'),
        requestData,
        { headers }
      );
      if (response.data?.success) {
        alert('Assessment created successfully!');
        setShowAssessmentForm(false);
        setAssessmentForm({ title: '', description: '', type: 'Technical', duration: '30 min', questions: [] });
        setQuestionPdfFile(null);
        // Refresh assessments list
        const assessmentsResponse = await axios.get(
          buildApiUrl('/api/moringa/assessments'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (assessmentsResponse.data?.success) {
          setAssessments(assessmentsResponse.data.assessments || []);
        }
      }
    } catch (error) {
      console.error('Failed to create assessment:', error);
      alert(error.response?.data?.error || 'Failed to create assessment. Please try again.');
    }
  };

  // Handle assign assessment
  const handleAssignAssessment = async (assessmentId, studentIds) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        buildApiUrl(`/api/moringa/assessments/${assessmentId}/assign`),
        { studentIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        alert(`Assessment assigned to ${studentIds.length} student(s) successfully!`);
        setShowAssignModal(false);
      }
    } catch (error) {
      console.error('Failed to assign assessment:', error);
      alert(error.response?.data?.error || 'Failed to assign assessment. Please try again.');
    }
  };

  // Fetch graduate categorisation
  useEffect(() => {
    const fetchGraduates = async () => {
      if (activeSection !== 'graduate-categorisation') return;

      try {
        setGraduatesLoading(true);
        const token = localStorage.getItem('token');
        // This would be a new endpoint: /api/moringa/graduates
        const response = await axios.get(
          buildApiUrl('/api/moringa/graduates'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setGraduates(response.data.graduates || []);
        }
      } catch (error) {
        console.error('Failed to fetch graduates:', error);
        setGraduates([]);
      } finally {
        setGraduatesLoading(false);
      }
    };

    fetchGraduates();
  }, [activeSection]);

  // Fetch mock interview resources
  useEffect(() => {
    const fetchMockInterviewResources = async () => {
      if (activeSection !== 'mock-interviews') return;

      try {
        setMockInterviewLoading(true);
        const token = localStorage.getItem('token');
        // This would be a new endpoint: /api/moringa/mock-interviews
        const response = await axios.get(
          buildApiUrl('/api/moringa/mock-interviews'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setMockInterviewResources(response.data.resources || []);
          setMockInterviews(response.data.sessions || []);
        }
      } catch (error) {
        console.error('Failed to fetch mock interview resources:', error);
        // Department-specific and general interview resources
        setMockInterviewResources([
          { id: 1, title: 'Business & Management Interview Prep', type: 'video', duration: '40 min', department: 'Business' },
          { id: 2, title: 'Technical & Engineering Interview Prep', type: 'video', duration: '45 min', department: 'Technical' },
          { id: 3, title: 'Creative Arts Interview Prep', type: 'video', duration: '35 min', department: 'Creative' },
          { id: 4, title: 'Research & Academia Interview Guide', type: 'guide', duration: '30 min', department: 'Research' },
          { id: 5, title: 'General Interview Skills', type: 'video', duration: '30 min', department: 'All' },
          { id: 6, title: 'Behavioral Interview Guide', type: 'guide', duration: '30 min', department: 'All' },
          { id: 7, title: 'Common Interview Questions', type: 'document', duration: '20 min', department: 'All' },
          { id: 8, title: 'Case Study Interview Prep', type: 'guide', duration: '35 min', department: 'Business' }
        ]);
      } finally {
        setMockInterviewLoading(false);
      }
    };

    fetchMockInterviewResources();
  }, [activeSection]);

  // Fetch AksharJobs jobs
  useEffect(() => {
    const fetchAksharJobs = async () => {
      if (activeSection !== 'job-boards') return;

      try {
        setJobBoardLoading(true);
        const token = localStorage.getItem('token');
        // Fetch all jobs from AksharJobs - endpoint returns array directly
        const response = await axios.get(
          buildApiUrl('/api/jobs/get_jobs'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('📋 AksharJobs API Response:', response.data);
        
        // Handle different response formats
        let jobsData = [];
        if (Array.isArray(response.data)) {
          jobsData = response.data;
        } else if (response.data && Array.isArray(response.data.jobs)) {
          jobsData = response.data.jobs;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          jobsData = response.data.data;
        }
        
        console.log(`✅ Fetched ${jobsData.length} jobs from AksharJobs`);
        setAksharJobs(jobsData);
      } catch (error) {
        console.error('❌ Failed to fetch AksharJobs:', error);
        console.error('Error details:', error.response?.data || error.message);
        setAksharJobs([]);
      } finally {
        setJobBoardLoading(false);
      }
    };

    fetchAksharJobs();
  }, [activeSection]);

  // Fetch CV Booklets
  useEffect(() => {
    const fetchCvBooklets = async () => {
      if (activeSection !== 'cv-booklets') return;

      try {
        setCvBookletsLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/moringa/cv-booklets'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setCvBooklets(response.data.booklets || []);
        }
      } catch (error) {
        console.error('Failed to fetch CV booklets:', error);
        setCvBooklets([]);
      } finally {
        setCvBookletsLoading(false);
      }
    };

    fetchCvBooklets();
  }, [activeSection]);

  // Fetch Employer Partner Events
  useEffect(() => {
    const fetchEmployerEvents = async () => {
      if (activeSection !== 'employer-events') return;

      try {
        setEmployerEventsLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/moringa/employer-events'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setEmployerEvents(response.data.events || []);
        }
      } catch (error) {
        console.error('Failed to fetch employer events:', error);
        setEmployerEvents([]);
      } finally {
        setEmployerEventsLoading(false);
      }
    };

    fetchEmployerEvents();
  }, [activeSection]);

  // Fetch students when Students section is active
  useEffect(() => {
    const fetchStudentsList = async () => {
      if (activeSection !== 'students') return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/moringa/students'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          setStudents(response.data.students || []);
        }
      } catch (error) {
        console.error('Failed to fetch students:', error);
      }
    };
    
    fetchStudentsList();
  }, [activeSection]);

  // Fetch Alumni Stories when section is active
  useEffect(() => {
    const fetchAlumniStories = async () => {
      if (activeSection !== 'alumni-stories') return;
      
      try {
        setAlumniStoriesLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/moringa/alumni-stories'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          setAlumniStories(response.data.stories || []);
        }
      } catch (error) {
        console.error('Failed to fetch alumni stories:', error);
        setAlumniStories([]);
      } finally {
        setAlumniStoriesLoading(false);
      }
    };
    
    fetchAlumniStories();
  }, [activeSection]);

  // Fetch First Destination Survey responses when section is active
  useEffect(() => {
    const fetchSurveyData = async () => {
      if (activeSection !== 'first-destination-survey') return;
      
      try {
        setSurveyLoading(true);
        const token = localStorage.getItem('token');
        
        const [responsesResponse, statsResponse] = await Promise.all([
          axios.get(
            buildApiUrl('/api/moringa/first-destination-survey/responses'),
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            buildApiUrl('/api/moringa/first-destination-survey/statistics'),
            { headers: { Authorization: `Bearer ${token}` } }
          )
        ]);
        
        if (responsesResponse.data?.success) {
          setSurveyResponses(responsesResponse.data.responses || []);
        }
        if (statsResponse.data?.success) {
          setSurveyStatistics(statsResponse.data.statistics);
        }
      } catch (error) {
        console.error('Failed to fetch survey data:', error);
        setSurveyResponses([]);
        setSurveyStatistics(null);
      } finally {
        setSurveyLoading(false);
      }
    };
    
    fetchSurveyData();
  }, [activeSection]);

  // Render Dashboard section
  const renderDashboard = () => (
    <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard
          title="Total Students"
          value={dashboardData.stats.totalStudents}
          trend={0}
          trendValue=""
          trendLabel=""
          icon={faUsers}
          iconColor="#f97316"
        />
        <StatCard
          title="Job Applications"
          value={dashboardData.stats.jobApplications}
          trend={0}
          trendValue=""
          trendLabel=""
          icon={faBriefcase}
          iconColor="#3b82f6"
        />
        <StatCard
          title="Coaching Sessions"
          value={dashboardData.stats.coachingSessions}
          trend={0}
          trendValue=""
          trendLabel=""
          icon={faUserTie}
          iconColor="#10b981"
        />
        <StatCard
          title="Placement Rate"
          value={`${dashboardData.stats.placementRate}%`}
          trend={0}
          trendValue=""
          trendLabel=""
          icon={faTrophy}
          iconColor="#10b981"
        />
        <StatCard
          title="Completed Assessments"
          value={dashboardData.stats.completedAssessments}
          trend={0}
          trendValue=""
          trendLabel=""
          icon={faBrain}
          iconColor="#8b5cf6"
        />
        <StatCard
          title="Mock Interviews"
          value={dashboardData.stats.mockInterviews}
          trend={0}
          trendValue=""
          trendLabel=""
          icon={faVideo}
          iconColor="#ec4899"
        />
      </div>

      {/* Quick Access Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
        <div 
          className="section-card" 
          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
          onClick={() => setActiveSection('career-coaching')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <FontAwesomeIcon icon={faUserTie} style={{ fontSize: '2rem', color: '#f97316', marginBottom: '1rem' }} />
          <h3>Career Coaching & Prep</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
            Access career planning modules and schedule personalized coaching sessions
          </p>
        </div>

        <div 
          className="section-card" 
          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
          onClick={() => setActiveSection('job-applications')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '2rem', color: '#3b82f6', marginBottom: '1rem' }} />
          <h3>Job Applications Tracking</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
            Track and manage student job applications across all platforms
          </p>
        </div>

        <div 
          className="section-card" 
          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
          onClick={() => setActiveSection('interview-tracking')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: '2rem', color: '#10b981', marginBottom: '1rem' }} />
          <h3>Interview Progress</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
            Monitor interview progress and outcomes for all students
          </p>
        </div>

        <div 
          className="section-card" 
          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
          onClick={() => setActiveSection('aptitude-assessment')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <FontAwesomeIcon icon={faBrain} style={{ fontSize: '2rem', color: '#8b5cf8', marginBottom: '1rem' }} />
          <h3>Aptitude Assessment</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
            Conduct and track aptitude assessments for career matching
          </p>
        </div>

        <div 
          className="section-card" 
          style={{ cursor: 'pointer', transition: 'transform 0.2s', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}
          onClick={() => setActiveSection('analytics')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '2rem', color: '#f59e0b', marginBottom: '1rem' }} />
          <h3>Success Analytics & Impact</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
            Comprehensive analytics showing placement rates, student outcomes, and program ROI
          </p>
        </div>

        <div 
          className="section-card" 
          style={{ cursor: 'pointer', transition: 'transform 0.2s', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }}
          onClick={() => setActiveSection('employer-events')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <FontAwesomeIcon icon={faHandshake} style={{ fontSize: '2rem', color: '#3b82f6', marginBottom: '1rem' }} />
          <h3>Employer Partnerships</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
            Manage corporate partnerships, networking events, and employer relations
          </p>
        </div>

        <div 
          className="section-card" 
          style={{ cursor: 'pointer', transition: 'transform 0.2s', background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)' }}
          onClick={() => setActiveSection('cv-booklets')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '2rem', color: '#ec4899', marginBottom: '1rem' }} />
          <h3>CV Booklet Generator</h3>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
            Create professional CV booklets to showcase students to employer partners
          </p>
        </div>
      </div>
    </div>
  );

  // Handle edit coaching session
  const handleEditCoachingSession = (session) => {
    setEditingCoachingSession(session);
    
    // Format date for input field (YYYY-MM-DD format)
    let formattedDate = session.date || '';
    if (formattedDate && formattedDate.includes('T')) {
      formattedDate = formattedDate.split('T')[0];
    } else if (formattedDate && formattedDate.includes(' ')) {
      formattedDate = formattedDate.split(' ')[0];
    }
    
    // Convert time from 24-hour to 12-hour format
    const time12 = convert24to12(session.time || '12:00');
    
    setCoachingForm({
      studentId: session.studentId || '',
      coachId: session.coachId || '',
      date: formattedDate,
      time: session.time || '',
      hour: time12.hour,
      minute: time12.minute,
      ampm: time12.ampm,
      type: session.type || 'Career Planning',
      notes: session.notes || ''
    });
    setShowCoachingForm(true);
  };

  // Handle delete coaching session
  const handleDeleteCoachingSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this coaching session?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        buildApiUrl(`/api/moringa/career-coaching/sessions/${sessionId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.success) {
        alert('Coaching session deleted successfully!');
        
        // Refresh sessions
        const modulesResponse = await axios.get(
          buildApiUrl('/api/moringa/career-coaching/modules'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (modulesResponse.data?.success) {
          setCoachingSessions(modulesResponse.data.sessions || []);
        }
      } else {
        alert(response.data?.error || 'Failed to delete session. Please try again.');
      }
    } catch (error) {
      console.error('Failed to delete coaching session:', error);
      alert(error.response?.data?.error || 'Failed to delete session. Please try again.');
    }
  };

  // Render Career Coaching section
  const renderCareerCoaching = () => (
    <div className="section-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2>Career Coaching & Preparation</h2>
          <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px' }}>
            Manage career planning modules and schedule personalized coaching sessions for students.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
                  onClick={() => {
                    setEditingCoachingSession(null);
                    setCoachingForm({
                      studentId: '',
                      coachId: '',
                      date: '',
                      time: '',
                      hour: '12',
                      minute: '00',
                      ampm: 'PM',
                      type: 'Career Planning',
                      notes: ''
                    });
                    setShowCoachingForm(true);
                  }}
          style={{ 
            width: '140px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          <FontAwesomeIcon icon={faCalendar} />
          <span>Schedule Session</span>
        </button>
      </div>

      {/* Career Planning Modules */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
          Career Planning Modules
        </h3>
        {coachingLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {coachingModules.map((module) => (
              <div
                key={module.id}
                style={{
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <FontAwesomeIcon icon={faBookOpen} style={{ color: '#f97316', fontSize: '1.25rem' }} />
                  <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                    {module.title}
                  </h4>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
                  {module.description}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{module.duration}</span>
                  <span style={{
                    padding: '0.25rem 0.6rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: module.status === 'available' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                    color: module.status === 'available' ? '#10b981' : '#6b7280'
                  }}>
                    {module.status === 'available' ? 'Available' : 'In Progress'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scheduled Coaching Sessions */}
      <div>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
          Scheduled Coaching Sessions
        </h3>
        {coachingSessions.length === 0 ? (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '2rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '0.9rem', color: '#64748b' }}>No coaching sessions scheduled yet.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {coachingSessions.map((session) => (
              <div
                key={session.id}
                style={{
                  padding: '1rem 1.25rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                    {session.studentName} - {session.type}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {session.date} at {formatTimeToAMPM(session.time)} with {session.coachName}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    padding: '0.25rem 0.6rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: session.status === 'scheduled' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    color: session.status === 'scheduled' ? '#3b82f6' : '#10b981'
                  }}>
                    {session.status}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleEditCoachingSession(session)}
                    style={{
                      width: '140px',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      color: '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    <span>Edit</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteCoachingSession(session.id)}
                    style={{
                      width: '140px',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      color: '#dc2626',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#fecaca';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#fee2e2';
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coaching Session Form Modal */}
      {showCoachingForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCoachingForm(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '500px',
              padding: '2rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                {editingCoachingSession ? 'Edit Coaching Session' : 'Schedule Coaching Session'}
              </h3>
              <button
                type="button"
                onClick={() => setShowCoachingForm(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Student
                </label>
                <select
                  value={coachingForm.studentId}
                  onChange={(e) => setCoachingForm(prev => ({ ...prev, studentId: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">Select Student</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.name} - {student.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Session Type
                </label>
                <select
                  value={coachingForm.type}
                  onChange={(e) => setCoachingForm(prev => ({ ...prev, type: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="Career Planning">Career Planning</option>
                  <option value="Resume Review">Resume Review</option>
                  <option value="Interview Prep">Interview Prep</option>
                  <option value="Salary Negotiation">Salary Negotiation</option>
                </select>
              </div>
              {/* Date Input */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Date
                </label>
                <input
                  type="date"
                  value={coachingForm.date}
                  onChange={(e) => setCoachingForm(prev => ({ ...prev, date: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Time Selector - Horizontal Layout directly under Date */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                  Time
                </label>
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  alignItems: 'center',
                  flexWrap: 'nowrap'
                }}>
                  {/* Hour Selector */}
                  <select
                    value={coachingForm.hour}
                    onChange={(e) => setCoachingForm(prev => ({ ...prev, hour: e.target.value }))}
                    style={{
                      flex: '0 0 70px',
                      padding: '0.625rem 0.5rem',
                      borderRadius: '6px',
                      border: '2px solid #d1d5db',
                      fontSize: '1rem',
                      fontWeight: 700,
                      background: '#ffffff',
                      color: '#000000',
                      cursor: 'pointer',
                      minHeight: '38px',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box',
                      appearance: 'auto',
                      WebkitAppearance: 'menulist',
                      MozAppearance: 'menulist'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <option key={h} value={String(h)} style={{ color: '#000000', backgroundColor: '#ffffff' }}>
                        {h}
                      </option>
                    ))}
                  </select>
                  
                  <span style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 700, 
                    color: '#374151',
                    lineHeight: '38px',
                    padding: '0 0.25rem'
                  }}>
                    :
                  </span>
                  
                  {/* Minute Selector */}
                  <select
                    value={coachingForm.minute}
                    onChange={(e) => setCoachingForm(prev => ({ ...prev, minute: e.target.value }))}
                    style={{
                      flex: '0 0 70px',
                      padding: '0.625rem 0.5rem',
                      borderRadius: '6px',
                      border: '2px solid #d1d5db',
                      fontSize: '1rem',
                      fontWeight: 700,
                      background: '#ffffff',
                      color: '#000000',
                      cursor: 'pointer',
                      minHeight: '38px',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box',
                      appearance: 'auto',
                      WebkitAppearance: 'menulist',
                      MozAppearance: 'menulist'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
                      <option key={m} value={m} style={{ color: '#000000', backgroundColor: '#ffffff' }}>
                        {m}
                      </option>
                    ))}
                  </select>
                  
                  {/* AM/PM Selector */}
                  <select
                    value={coachingForm.ampm}
                    onChange={(e) => setCoachingForm(prev => ({ ...prev, ampm: e.target.value }))}
                    style={{
                      flex: '0 0 80px',
                      padding: '0.625rem 0.5rem',
                      borderRadius: '6px',
                      border: '2px solid #d1d5db',
                      fontSize: '1rem',
                      fontWeight: 700,
                      background: '#ffffff',
                      color: '#000000',
                      cursor: 'pointer',
                      minHeight: '38px',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box',
                      appearance: 'auto',
                      WebkitAppearance: 'menulist',
                      MozAppearance: 'menulist'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="AM" style={{ color: '#000000', backgroundColor: '#ffffff' }}>AM</option>
                    <option value="PM" style={{ color: '#000000', backgroundColor: '#ffffff' }}>PM</option>
                  </select>
                </div>
                
                {/* Display Selected Time */}
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '0.5rem 0.75rem', 
                  background: '#f3f4f6', 
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}>
                  <span style={{ color: '#6b7280', marginRight: '0.5rem' }}>Selected:</span>
                  <span style={{ fontWeight: 700, color: '#111827' }}>
                    {coachingForm.hour}:{coachingForm.minute} {coachingForm.ampm}
                  </span>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Notes
                </label>
                <textarea
                  value={coachingForm.notes}
                  onChange={(e) => setCoachingForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCoachingForm(false);
                    setEditingCoachingSession(null);
                    setCoachingForm({
                      studentId: '',
                      coachId: '',
                      date: '',
                      time: '',
                      hour: '12',
                      minute: '00',
                      ampm: 'PM',
                      type: 'Career Planning',
                      notes: ''
                    });
                  }}
                  style={{ 
                    width: '140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ 
                    width: '140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      
                      // Convert 12-hour format to 24-hour format for storage
                      const time24 = convert12to24(coachingForm.hour, coachingForm.minute, coachingForm.ampm);
                      
                      // Prepare form data with 24-hour time
                      const formData = {
                        ...coachingForm,
                        time: time24
                      };
                      
                      if (editingCoachingSession) {
                        // Update existing session
                        const response = await axios.put(
                          buildApiUrl(`/api/moringa/career-coaching/sessions/${editingCoachingSession.id}`),
                          formData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        if (response.data?.success) {
                          alert('Coaching session updated successfully!');
                          setShowCoachingForm(false);
                          setEditingCoachingSession(null);
                          setCoachingForm({
                            studentId: '',
                            coachId: '',
                            date: '',
                            time: '',
                            hour: '12',
                            minute: '00',
                            ampm: 'PM',
                            type: 'Career Planning',
                            notes: ''
                          });
                          // Refresh sessions
                          setTimeout(async () => {
                            try {
                              const modulesResponse = await axios.get(
                                buildApiUrl('/api/moringa/career-coaching/modules'),
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              if (modulesResponse.data?.success) {
                                setCoachingSessions(modulesResponse.data.sessions || []);
                              }
                            } catch (refreshError) {
                              console.error('Failed to refresh sessions:', refreshError);
                            }
                          }, 500);
                        }
                      } else {
                        // Create new session
                        const response = await axios.post(
                          buildApiUrl('/api/moringa/career-coaching/sessions'),
                          formData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        if (response.data?.success) {
                          alert('Coaching session scheduled successfully!');
                          setShowCoachingForm(false);
                          setCoachingForm({
                            studentId: '',
                            coachId: '',
                            date: '',
                            time: '',
                            hour: '12',
                            minute: '00',
                            ampm: 'PM',
                            type: 'Career Planning',
                            notes: ''
                          });
                          // Refresh sessions - add a small delay to ensure DB write is complete
                          setTimeout(async () => {
                            try {
                              const modulesResponse = await axios.get(
                                buildApiUrl('/api/moringa/career-coaching/modules'),
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              if (modulesResponse.data?.success) {
                                console.log('📊 Refreshed coaching sessions:', modulesResponse.data.sessions);
                                setCoachingSessions(modulesResponse.data.sessions || []);
                              }
                            } catch (refreshError) {
                              console.error('Failed to refresh sessions:', refreshError);
                            }
                          }, 500);
                        }
                      }
                    } catch (error) {
                      console.error('Failed to save coaching session:', error);
                      alert(error.response?.data?.error || 'Failed to save session. Please try again.');
                    }
                  }}
                >
                  <span>{editingCoachingSession ? 'Update Session' : 'Schedule Session'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Job Applications Tracking section
  const renderJobApplications = () => {
    const filteredApplications = jobApplications.filter(app => {
      // Filter by status
      if (applicationFilters.status && app.status !== applicationFilters.status) return false;
      
      // Combined search across student name, company name, and position
      if (applicationFilters.search) {
        const searchTerm = applicationFilters.search.toLowerCase();
        const matchesStudent = app.studentName?.toLowerCase().includes(searchTerm);
        const matchesCompany = app.companyName?.toLowerCase().includes(searchTerm);
        const matchesPosition = app.position?.toLowerCase().includes(searchTerm) || app.jobTitle?.toLowerCase().includes(searchTerm);
        
        if (!matchesStudent && !matchesCompany && !matchesPosition) return false;
      }
      
      return true;
    });

    return (
      <div className="section-card">
        <h2>Job Applications Tracking</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          Track and monitor all job applications submitted by students across integrated job boards and employer networks.
        </p>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          marginBottom: '1.5rem', 
          alignItems: 'center',
          flexWrap: 'nowrap'
        }}>
          <input
            type="text"
            placeholder="Search applications by student, company, or position..."
            value={applicationFilters.search}
            onChange={(e) => setApplicationFilters(prev => ({ ...prev, search: e.target.value }))}
            style={{
              width: '200px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem'
            }}
          />
          <select
            className="filter-select"
            value={applicationFilters.status}
            onChange={(e) => setApplicationFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{
              width: '120px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Statuses</option>
            <option value="Applied">Applied</option>
            <option value="Under Review">Under Review</option>
            <option value="Interview">Interview</option>
            <option value="Offer">Offer</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        {/* Applications Table */}
        {applicationsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : filteredApplications.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b' }}>No applications found</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Company</th>
                  <th>Position</th>
                  <th>Applied Date</th>
                  <th>Status</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((app) => (
                  <tr key={app.id}>
                    <td>{app.studentName}</td>
                    <td>{app.companyName}</td>
                    <td>{app.position}</td>
                    <td>{app.appliedDate}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: app.status === 'Offer' ? 'rgba(16, 185, 129, 0.1)' :
                                   app.status === 'Interview' ? 'rgba(59, 130, 246, 0.1)' :
                                   app.status === 'Rejected' ? 'rgba(248, 113, 113, 0.1)' : 'rgba(249, 115, 22, 0.1)',
                        color: app.status === 'Offer' ? '#10b981' :
                               app.status === 'Interview' ? '#3b82f6' :
                               app.status === 'Rejected' ? '#f87171' : '#f97316'
                      }}>
                        {app.status}
                      </span>
                    </td>
                    <td>{app.source || 'Direct'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render Interview Progress Tracking section
  const renderInterviewTracking = () => {
    const filteredInterviews = interviews.filter(interview => {
      if (interviewFilters.status && interview.status !== interviewFilters.status) return false;
      if (interviewFilters.student && !interview.studentName?.toLowerCase().includes(interviewFilters.student.toLowerCase())) return false;
      return true;
    });

    return (
      <div className="section-card">
        <h2>Interview Progress Tracking</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          Monitor interview progress, outcomes, and feedback for all student interviews.
        </p>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          marginBottom: '1.5rem', 
          alignItems: 'center',
          flexWrap: 'nowrap'
        }}>
          <input
            type="text"
            placeholder="Search by student name..."
            value={interviewFilters.student}
            onChange={(e) => setInterviewFilters(prev => ({ ...prev, student: e.target.value }))}
            style={{
              width: '200px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem'
            }}
          />
          <select
            className="filter-select"
            value={interviewFilters.status}
            onChange={(e) => setInterviewFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{
              width: '120px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Statuses</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Passed">Passed</option>
            <option value="Failed">Failed</option>
          </select>
        </div>

        {/* Interviews List */}
        {interviewsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : filteredInterviews.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b' }}>No interviews found</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredInterviews.map((interview) => (
              <div
                key={interview.id}
                style={{
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                    {interview.studentName} - {interview.companyName}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {interview.position} • {interview.date} at {interview.time}
                  </div>
                  {interview.feedback && (
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                      Feedback: {interview.feedback}
                    </div>
                  )}
                </div>
                <span style={{
                  padding: '0.25rem 0.6rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  background: interview.status === 'Passed' ? 'rgba(16, 185, 129, 0.1)' :
                             interview.status === 'Failed' ? 'rgba(248, 113, 113, 0.1)' :
                             interview.status === 'Scheduled' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                  color: interview.status === 'Passed' ? '#10b981' :
                         interview.status === 'Failed' ? '#f87171' :
                         interview.status === 'Scheduled' ? '#3b82f6' : '#6b7280'
                }}>
                  {interview.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render Aptitude Assessment section
  const renderAptitudeAssessment = () => {
    const handleOpenAssignModal = async (assessment) => {
      setSelectedAssessment(assessment);
      // Only fetch if modal is opening for the first time or students list is empty
      if (assessmentStudents.length === 0) {
        await fetchAssessmentStudents();
      }
      setShowAssignModal(true);
    };

    const handleOpenResultsModal = async (assessment) => {
      setSelectedAssessment(assessment);
      await fetchAssessmentResults(assessment.id);
      setShowResultsModal(true);
    };

    return (
      <div className="section-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2>Aptitude Assessment</h2>
            <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px' }}>
              Conduct and track aptitude assessments to help categorize graduates and match them with suitable career paths.
            </p>
          </div>
          <button
            onClick={() => setShowAssessmentForm(true)}
            style={{
              width: '140px',
              padding: '0.625rem 1rem',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#ea580c'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f97316'}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Create Custom</span>
          </button>
        </div>

        {assessmentsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : assessments.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faBrain} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>No assessments available</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Create assessments to evaluate student aptitudes and career readiness.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {assessments.map((assessment) => (
              <div
                key={assessment.id}
                style={{
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#f97316';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <FontAwesomeIcon icon={faBrain} style={{ color: '#8b5cf6', fontSize: '1.25rem' }} />
                  <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                    {assessment.title}
                  </h4>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
                  {assessment.description}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    {assessment.completedCount || 0} completed
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {assessment.hasPdfQuestions && (
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        📄 PDF
                      </span>
                    )}
                    <span style={{
                      padding: '0.25rem 0.6rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      background: 'rgba(139, 92, 246, 0.1)',
                      color: '#8b5cf6'
                    }}>
                      {assessment.duration || '30 min'}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenAssignModal(assessment);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      background: '#f97316',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FontAwesomeIcon icon={faUserCheck} />
                    <span>Assign</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenResultsModal(assessment);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FontAwesomeIcon icon={faChartLine} />
                    <span>Track</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Assessment Modal */}
        {showAssessmentForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="assessment-form-container" style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#0f172a' }}>Create Custom Assessment</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                    Assessment Title *
                  </label>
                  <input
                    type="text"
                    value={assessmentForm.title}
                    onChange={(e) => setAssessmentForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Python Programming Assessment"
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                    Description
                  </label>
                  <textarea
                    value={assessmentForm.description}
                    onChange={(e) => setAssessmentForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this assessment evaluates..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.875rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                      Type *
                    </label>
                    <select
                      value={assessmentForm.type}
                      onChange={(e) => setAssessmentForm(prev => ({ ...prev, type: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="Technical">Technical</option>
                      <option value="Communication">Communication</option>
                      <option value="Career Readiness">Career Readiness</option>
                      <option value="General Aptitude">General Aptitude</option>
                      <option value="Soft Skills">Soft Skills</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                      Duration
                    </label>
                    <input
                      type="text"
                      value={assessmentForm.duration}
                      onChange={(e) => setAssessmentForm(prev => ({ ...prev, duration: e.target.value }))}
                      placeholder="e.g., 45 min"
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                </div>
                
                {/* PDF Upload Section */}
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>
                    📄 Upload Question Paper (PDF) - Optional
                  </label>
                  <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.75rem' }}>
                    Upload a PDF file containing your assessment questions. This is an easier alternative to manually entering questions.
                  </p>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.type !== 'application/pdf') {
                          alert('Please upload a PDF file only.');
                          e.target.value = '';
                          return;
                        }
                        if (file.size > 10 * 1024 * 1024) { // 10MB limit
                          alert('File size should be less than 10MB.');
                          e.target.value = '';
                          return;
                        }
                        setQuestionPdfFile(file);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid #bfdbfe',
                      fontSize: '0.875rem',
                      background: 'white'
                    }}
                  />
                  {questionPdfFile && (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#dbeafe', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                        ✓ {questionPdfFile.name} ({(questionPdfFile.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setQuestionPdfFile(null);
                        }}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }}>
                    💡 Tip: If you upload a PDF, you can skip manually entering questions below. Students will receive the PDF to answer.
                  </p>
                </div>
                
                {/* Questions Section */}
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <label style={{ fontWeight: 600, color: '#374151' }}>
                      Assessment Questions ({assessmentForm.questions.length}) {questionPdfFile && <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }}>(Optional - PDF uploaded)</span>}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setAssessmentForm(prev => ({
                          ...prev,
                          questions: [...prev.questions, {
                            question: '',
                            type: 'multiple-choice',
                            options: ['', '', '', ''],
                            correctAnswer: 0,
                            points: 1
                          }]
                        }));
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Add Question</span>
                    </button>
                  </div>
                  
                  {assessmentForm.questions.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem', padding: '1rem' }}>
                      No questions added yet. Click "Add Question" to create questions for this assessment.
                      <br />
                      <span style={{ fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
                        Note: Students will be able to take this assessment from their dashboard once assigned.
                      </span>
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '300px', overflowY: 'auto' }}>
                      {assessmentForm.questions.map((q, index) => (
                        <div key={index} style={{ padding: '1rem', background: 'white', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>Question {index + 1}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setAssessmentForm(prev => ({
                                  ...prev,
                                  questions: prev.questions.filter((_, i) => i !== index)
                                }));
                              }}
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                          <input
                            type="text"
                            value={q.question}
                            onChange={(e) => {
                              const newQuestions = [...assessmentForm.questions];
                              newQuestions[index].question = e.target.value;
                              setAssessmentForm(prev => ({ ...prev, questions: newQuestions }));
                            }}
                            placeholder="Enter your question here..."
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid #d1d5db',
                              fontSize: '0.875rem',
                              marginBottom: '0.75rem'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <select
                              value={q.type}
                              onChange={(e) => {
                                const newQuestions = [...assessmentForm.questions];
                                newQuestions[index].type = e.target.value;
                                setAssessmentForm(prev => ({ ...prev, questions: newQuestions }));
                              }}
                              style={{
                                flex: 1,
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #d1d5db',
                                fontSize: '0.75rem'
                              }}
                            >
                              <option value="multiple-choice">Multiple Choice</option>
                              <option value="true-false">True/False</option>
                              <option value="short-answer">Short Answer</option>
                            </select>
                            <input
                              type="number"
                              value={q.points}
                              onChange={(e) => {
                                const newQuestions = [...assessmentForm.questions];
                                newQuestions[index].points = parseInt(e.target.value) || 1;
                                setAssessmentForm(prev => ({ ...prev, questions: newQuestions }));
                              }}
                              placeholder="Points"
                              min="1"
                              style={{
                                width: '80px',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                border: '1px solid #d1d5db',
                                fontSize: '0.75rem'
                              }}
                            />
                          </div>
                          {(q.type === 'multiple-choice' || q.type === 'true-false') && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {q.type === 'true-false' ? (
                                <>
                                  {['True', 'False'].map((opt, optIndex) => (
                                    <label key={optIndex} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                      <input
                                        type="radio"
                                        name={`correct-${index}`}
                                        checked={q.correctAnswer === optIndex}
                                        onChange={() => {
                                          const newQuestions = [...assessmentForm.questions];
                                          newQuestions[index].correctAnswer = optIndex;
                                          setAssessmentForm(prev => ({ ...prev, questions: newQuestions }));
                                        }}
                                      />
                                      <span style={{ fontSize: '0.875rem' }}>{opt}</span>
                                    </label>
                                  ))}
                                </>
                              ) : (
                                <>
                                  {q.options.map((opt, optIndex) => (
                                    <div key={optIndex} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                      <input
                                        type="text"
                                        value={opt}
                                        onChange={(e) => {
                                          const newQuestions = [...assessmentForm.questions];
                                          newQuestions[index].options[optIndex] = e.target.value;
                                          setAssessmentForm(prev => ({ ...prev, questions: newQuestions }));
                                        }}
                                        placeholder={`Option ${optIndex + 1}`}
                                        style={{
                                          flex: 1,
                                          padding: '0.5rem',
                                          borderRadius: '4px',
                                          border: '1px solid #d1d5db',
                                          fontSize: '0.875rem'
                                        }}
                                      />
                                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.75rem' }}>
                                        <input
                                          type="radio"
                                          name={`correct-${index}`}
                                          checked={q.correctAnswer === optIndex}
                                          onChange={() => {
                                            const newQuestions = [...assessmentForm.questions];
                                            newQuestions[index].correctAnswer = optIndex;
                                            setAssessmentForm(prev => ({ ...prev, questions: newQuestions }));
                                          }}
                                        />
                                        Correct
                                      </label>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {assessmentForm.questions.length > 0 && (
                    <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }}>
                      💡 Students will be able to take this assessment from their dashboard once you assign it to them.
                    </p>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button
                    onClick={handleCreateAssessment}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#f97316',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Create Assessment
                  </button>
                  <button
                    onClick={() => {
                      setShowAssessmentForm(false);
                      setAssessmentForm({ title: '', description: '', type: 'Technical', duration: '30 min', questions: [] });
                      setQuestionPdfFile(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#e2e8f0',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assign Assessment Modal */}
        {showAssignModal && selectedAssessment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginBottom: '1rem', color: '#0f172a' }}>
                Assign: {selectedAssessment.title}
              </h3>
              <p style={{ marginBottom: '1.5rem', color: '#64748b', fontSize: '0.875rem' }}>
                Select students to assign this assessment to. They will be notified and can complete it.
              </p>
              <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
                {assessmentStudents.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>Loading students...</p>
                ) : (
                  assessmentStudents.map(student => (
                    <label
                      key={student.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginBottom: '0.5rem',
                        background: selectedStudents.includes(student.id) ? '#fef3c7' : 'transparent',
                        transition: 'background 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents(prev => [...prev, student.id]);
                          } else {
                            setSelectedStudents(prev => prev.filter(id => id !== student.id));
                          }
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{student.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{student.email} • {student.department}</div>
                      </div>
                    </label>
                  ))
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={async () => {
                    if (selectedStudents.length > 0) {
                      await handleAssignAssessment(selectedAssessment.id, selectedStudents);
                      setSelectedStudents([]);
                    } else {
                      alert('Please select at least one student');
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Assign to {selectedStudents.length} Student(s)
                </button>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedStudents([]);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#e2e8f0',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results/Tracking Modal */}
        {showResultsModal && selectedAssessment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '2rem',
              width: '90%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginBottom: '1rem', color: '#0f172a' }}>
                Track: {selectedAssessment.title}
              </h3>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ padding: '0.75rem 1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#166534', marginBottom: '0.25rem' }}>Completed</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#15803d' }}>
                    {assessmentResults.filter(r => r.status === 'completed').length}
                  </div>
                </div>
                <div style={{ padding: '0.75rem 1rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: '0.25rem' }}>In Progress</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#b45309' }}>
                    {assessmentResults.filter(r => r.status === 'in_progress').length}
                  </div>
                </div>
                <div style={{ padding: '0.75rem 1rem', background: '#f3f4f6', borderRadius: '8px', border: '1px solid #d1d5db' }}>
                  <div style={{ fontSize: '0.75rem', color: '#374151', marginBottom: '0.25rem' }}>Assigned</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>
                    {assessmentResults.filter(r => r.status === 'assigned').length}
                  </div>
                </div>
              </div>
              {assessmentResults.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>No students assigned yet</p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Completed Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessmentResults.map((result) => (
                        <tr key={result.assignmentId}>
                          <td>{result.studentName}</td>
                          <td>{result.studentEmail}</td>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.6rem',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              background: result.status === 'completed' ? 'rgba(16, 185, 129, 0.1)' :
                                         result.status === 'in_progress' ? 'rgba(249, 115, 22, 0.1)' :
                                         'rgba(156, 163, 175, 0.1)',
                              color: result.status === 'completed' ? '#10b981' :
                                     result.status === 'in_progress' ? '#f97316' :
                                     '#6b7280'
                            }}>
                              {result.status.charAt(0).toUpperCase() + result.status.slice(1).replace('_', ' ')}
                            </span>
                          </td>
                          <td>
                            {result.score !== null ? (
                              <span style={{ fontWeight: 600 }}>
                                {result.correctAnswers}/{result.totalQuestions} ({result.score}%)
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>-</span>
                            )}
                          </td>
                          <td>{result.completedAt ? new Date(result.completedAt).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button
                onClick={() => setShowResultsModal(false)}
                style={{
                  marginTop: '1.5rem',
                  width: '100%',
                  padding: '0.75rem',
                  background: '#e2e8f0',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Graduate Categorisation section (Enhanced with skill and experience-based categorization)
  const renderGraduateCategorisation = () => {
    const categories = ['Tech Ready', 'Business Ready', 'Creative', 'Research', 'Entrepreneurial', 'General'];
    const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
    const experienceLevels = ['Entry Level', '1-2 Years', '3-5 Years', '5+ Years'];
    
    const filteredGraduates = graduates.filter(graduate => {
      if (categoryFilters.category && graduate.category !== categoryFilters.category) return false;
      if (categoryFilters.department && graduate.department !== categoryFilters.department) return false;
      if (categoryFilters.year && graduate.graduationYear !== categoryFilters.year) return false;
      if (categoryFilters.skillLevel && graduate.skillLevel !== categoryFilters.skillLevel) return false;
      if (categoryFilters.experience && graduate.experienceLevel !== categoryFilters.experience) return false;
      return true;
    });

    return (
      <div className="section-card">
        <h2>Candidate Categorisation</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          Categorize candidates based on their skills, experience level, assessments, and career readiness to better match them with employer opportunities. Filter by skill level (Beginner to Expert) and experience (Entry Level to 5+ Years).
        </p>

        {/* Enhanced Filters - Category, Skill Level, Experience */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          marginBottom: '1.5rem', 
          alignItems: 'center',
          flexWrap: 'nowrap'
        }}>
          <select
            className="filter-select"
            value={categoryFilters.category}
            onChange={(e) => setCategoryFilters(prev => ({ ...prev, category: e.target.value }))}
            style={{
              width: '140px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={categoryFilters.department}
            onChange={(e) => setCategoryFilters(prev => ({ ...prev, department: e.target.value }))}
            style={{
              width: '140px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Departments</option>
          </select>
          <select
            className="filter-select"
            value={categoryFilters.year}
            onChange={(e) => setCategoryFilters(prev => ({ ...prev, year: e.target.value }))}
            style={{
              width: '120px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Years</option>
          </select>
          <select
            className="filter-select"
            value={categoryFilters.skillLevel}
            onChange={(e) => setCategoryFilters(prev => ({ ...prev, skillLevel: e.target.value }))}
            style={{
              width: '140px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Skill Levels</option>
            {skillLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={categoryFilters.experience}
            onChange={(e) => setCategoryFilters(prev => ({ ...prev, experience: e.target.value }))}
            style={{
              width: '160px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Experience Levels</option>
            {experienceLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>

        {/* Graduates List */}
        {graduatesLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : filteredGraduates.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faTags} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b' }}>No graduates found</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Department</th>
                  <th>Graduation Year</th>
                  <th>Category</th>
                  <th>Skill Level</th>
                  <th>Experience</th>
                  <th>Top Skills</th>
                  <th>Career Readiness</th>
                </tr>
              </thead>
              <tbody>
                {filteredGraduates.map((graduate) => (
                  <tr key={graduate.id}>
                    <td>{graduate.name}</td>
                    <td>{graduate.department}</td>
                    <td>{graduate.graduationYear}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(139, 92, 246, 0.1)',
                        color: '#8b5cf6'
                      }}>
                        {graduate.category || 'General'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: graduate.skillLevel === 'Expert' ? 'rgba(16, 185, 129, 0.1)' :
                                   graduate.skillLevel === 'Advanced' ? 'rgba(59, 130, 246, 0.1)' :
                                   graduate.skillLevel === 'Intermediate' ? 'rgba(249, 115, 22, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                        color: graduate.skillLevel === 'Expert' ? '#10b981' :
                               graduate.skillLevel === 'Advanced' ? '#3b82f6' :
                               graduate.skillLevel === 'Intermediate' ? '#f97316' : '#6b7280'
                      }}>
                        {graduate.skillLevel || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(139, 92, 246, 0.1)',
                        color: '#8b5cf6'
                      }}>
                        {graduate.experienceLevel || 'Entry Level'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {(graduate.topSkills || []).slice(0, 3).map((skill, idx) => (
                          <span key={idx} style={{
                            padding: '0.15rem 0.4rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            background: '#f3f4f6',
                            color: '#64748b'
                          }}>
                            {skill}
                          </span>
                        ))}
                        {(graduate.topSkills || []).length > 3 && (
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                            +{(graduate.topSkills || []).length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          flex: 1,
                          height: '8px',
                          borderRadius: '999px',
                          background: '#e5e7eb',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${graduate.careerReadiness || 0}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, rgba(16,185,129,1) 0%, rgba(59,130,246,1) 100%)'
                          }} />
                        </div>
                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0f172a', minWidth: '40px' }}>
                          {graduate.careerReadiness || 0}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render Mock Interview Prep section
  const renderMockInterviews = () => (
    <div className="section-card">
      <h2>Mock Interview Preparation Resources</h2>
      <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
        Access mock interview resources tailored for all departments (Technical, Business, Creative, Research) and general interview skills. Track mock interview sessions to help students prepare for real interviews.
      </p>

      {/* Resources */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
            Interview Preparation Resources
          </h3>
          <select
            value={interviewResourceFilter}
            onChange={(e) => setInterviewResourceFilter(e.target.value)}
            style={{
              width: '180px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Departments</option>
            <option value="All">General (All Departments)</option>
            <option value="Technical">Technical & Engineering</option>
            <option value="Business">Business & Management</option>
            <option value="Creative">Creative Arts</option>
            <option value="Research">Research & Academia</option>
          </select>
        </div>
        {mockInterviewLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {mockInterviewResources
              .filter(resource => 
                interviewResourceFilter === 'all' || 
                resource.department === interviewResourceFilter ||
                (interviewResourceFilter === 'All' && resource.department === 'All')
              )
              .map((resource) => (
              <div
                key={resource.id}
                style={{
                  padding: '1.25rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <FontAwesomeIcon 
                    icon={resource.type === 'video' ? faVideo : resource.type === 'document' ? faFileAlt : faBookOpen} 
                    style={{ color: '#ec4899', fontSize: '1.25rem' }} 
                  />
                  <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                    {resource.title}
                  </h4>
                </div>
                {resource.department && resource.department !== 'All' && (
                  <span style={{
                    display: 'inline-block',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    background: resource.department === 'Technical' ? 'rgba(59, 130, 246, 0.1)' :
                               resource.department === 'Business' ? 'rgba(16, 185, 129, 0.1)' :
                               resource.department === 'Creative' ? 'rgba(236, 72, 153, 0.1)' :
                               resource.department === 'Research' ? 'rgba(139, 92, 246, 0.1)' :
                               'rgba(156, 163, 175, 0.1)',
                    color: resource.department === 'Technical' ? '#3b82f6' :
                           resource.department === 'Business' ? '#10b981' :
                           resource.department === 'Creative' ? '#ec4899' :
                           resource.department === 'Research' ? '#8b5cf6' :
                           '#6b7280',
                    marginBottom: '0.5rem'
                  }}>
                    {resource.department}
                  </span>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: resource.department && resource.department !== 'All' ? '0.5rem' : '0' }}>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{resource.duration}</span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ 
                      width: '140px', 
                      fontSize: '0.8rem', 
                      padding: '0.4rem 0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mock Interview Sessions */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
            Mock Interview Sessions
          </h3>
          <button
            onClick={() => setShowMockInterviewForm(true)}
            style={{
              width: '140px',
              padding: '0.5rem 0.75rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.75rem', flexShrink: 0 }} />
            <span style={{ fontSize: '0.75rem' }}>Schedule Session</span>
          </button>
        </div>
        {mockInterviews.length === 0 ? (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faVideo} style={{ fontSize: '2rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '0.9rem', color: '#64748b' }}>No mock interview sessions scheduled yet.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {mockInterviews.map((session) => (
              <div
                key={session.id}
                style={{
                  padding: '1rem 1.25rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                    {session.studentName} - {session.type}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {session.date} at {session.time} • Score: {session.score || 'Pending'}
                  </div>
                </div>
                <span style={{
                  padding: '0.25rem 0.6rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  background: session.status === 'completed' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                  color: session.status === 'completed' ? '#10b981' : '#3b82f6'
                }}>
                  {session.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Schedule Mock Interview Modal */}
      {showMockInterviewForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginBottom: '1.5rem', color: '#0f172a' }}>Schedule Mock Interview Session</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                  Student Name/Email *
                </label>
                <input
                  type="text"
                  value={mockInterviewForm.studentName}
                  onChange={(e) => setMockInterviewForm(prev => ({ ...prev, studentName: e.target.value }))}
                  placeholder="Search and select student..."
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                  Interview Type *
                </label>
                <select
                  value={mockInterviewForm.type}
                  onChange={(e) => setMockInterviewForm(prev => ({ ...prev, type: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="Technical">Technical Interview</option>
                  <option value="Behavioral">Behavioral Interview</option>
                  <option value="Business">Business Case Interview</option>
                  <option value="Creative">Creative Portfolio Review</option>
                  <option value="Research">Research Presentation</option>
                  <option value="General">General Interview</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                  Date *
                </label>
                <input
                  type="date"
                  value={mockInterviewForm.date}
                  onChange={(e) => setMockInterviewForm(prev => ({ ...prev, date: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Time Selector - Horizontal Layout */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                  Time *
                </label>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  flexWrap: 'nowrap'
                }}>
                  {/* Hour Selector */}
                  <select
                    value={mockInterviewForm.hour}
                    onChange={(e) => setMockInterviewForm(prev => ({ ...prev, hour: e.target.value }))}
                    style={{
                      flex: '0 0 70px',
                      padding: '0.625rem 0.5rem',
                      borderRadius: '6px',
                      border: '2px solid #d1d5db',
                      fontSize: '1rem',
                      fontWeight: 700,
                      background: '#ffffff',
                      color: '#000000',
                      cursor: 'pointer',
                      minHeight: '38px',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box',
                      appearance: 'auto',
                      WebkitAppearance: 'menulist',
                      MozAppearance: 'menulist'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#10b981';
                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <option key={h} value={String(h)} style={{ color: '#000000', backgroundColor: '#ffffff' }}>
                        {h}
                      </option>
                    ))}
                  </select>

                  <span style={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: '#374151',
                    lineHeight: '38px',
                    padding: '0 0.25rem'
                  }}>
                    :
                  </span>

                  {/* Minute Selector */}
                  <select
                    value={mockInterviewForm.minute}
                    onChange={(e) => setMockInterviewForm(prev => ({ ...prev, minute: e.target.value }))}
                    style={{
                      flex: '0 0 70px',
                      padding: '0.625rem 0.5rem',
                      borderRadius: '6px',
                      border: '2px solid #d1d5db',
                      fontSize: '1rem',
                      fontWeight: 700,
                      background: '#ffffff',
                      color: '#000000',
                      cursor: 'pointer',
                      minHeight: '38px',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box',
                      appearance: 'auto',
                      WebkitAppearance: 'menulist',
                      MozAppearance: 'menulist'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#10b981';
                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
                      <option key={m} value={m} style={{ color: '#000000', backgroundColor: '#ffffff' }}>
                        {m}
                      </option>
                    ))}
                  </select>

                  {/* AM/PM Selector */}
                  <select
                    value={mockInterviewForm.ampm}
                    onChange={(e) => setMockInterviewForm(prev => ({ ...prev, ampm: e.target.value }))}
                    style={{
                      flex: '0 0 80px',
                      padding: '0.625rem 0.5rem',
                      borderRadius: '6px',
                      border: '2px solid #d1d5db',
                      fontSize: '1rem',
                      fontWeight: 700,
                      background: '#ffffff',
                      color: '#000000',
                      cursor: 'pointer',
                      minHeight: '38px',
                      textAlign: 'center',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box',
                      appearance: 'auto',
                      WebkitAppearance: 'menulist',
                      MozAppearance: 'menulist'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#10b981';
                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="AM" style={{ color: '#000000', backgroundColor: '#ffffff' }}>AM</option>
                    <option value="PM" style={{ color: '#000000', backgroundColor: '#ffffff' }}>PM</option>
                  </select>
                </div>

                {/* Display Selected Time */}
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}>
                  <span style={{ color: '#6b7280', marginRight: '0.5rem' }}>Selected:</span>
                  <span style={{ fontWeight: 700, color: '#111827' }}>
                    {mockInterviewForm.hour}:{mockInterviewForm.minute} {mockInterviewForm.ampm}
                  </span>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={mockInterviewForm.duration}
                  onChange={(e) => setMockInterviewForm(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="30"
                  min="15"
                  max="120"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151' }}>
                  Notes/Instructions
                </label>
                <textarea
                  value={mockInterviewForm.notes}
                  onChange={(e) => setMockInterviewForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes or instructions for the student..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <button
                  onClick={async () => {
                    // Convert 12-hour time to 24-hour format
                    const convert12to24 = (hour, minute, ampm) => {
                      let h = parseInt(hour);
                      if (ampm === 'PM' && h !== 12) h += 12;
                      if (ampm === 'AM' && h === 12) h = 0;
                      return `${String(h).padStart(2, '0')}:${minute}`;
                    };

                    const time24 = convert12to24(mockInterviewForm.hour, mockInterviewForm.minute, mockInterviewForm.ampm);
                    
                    try {
                      const token = localStorage.getItem('token');
                      // TODO: Implement actual API endpoint for scheduling mock interviews
                      // For now, just show success message and add to local state
                      alert('Mock interview session scheduled successfully!');
                      setShowMockInterviewForm(false);
                      setMockInterviewForm({
                        studentId: '',
                        studentName: '',
                        type: 'Technical',
                        date: '',
                        hour: '12',
                        minute: '00',
                        ampm: 'PM',
                        duration: '30',
                        notes: ''
                      });
                    } catch (error) {
                      console.error('Failed to schedule mock interview:', error);
                      alert(error.response?.data?.error || 'Failed to schedule session. Please try again.');
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Schedule Session
                </button>
                <button
                  onClick={() => {
                    setShowMockInterviewForm(false);
                    setMockInterviewForm({
                      studentId: '',
                      studentName: '',
                      type: 'Technical',
                      date: '',
                      hour: '12',
                      minute: '00',
                      ampm: 'PM',
                      duration: '30',
                      notes: ''
                    });
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#e2e8f0',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Job Board Integration section
  const renderJobBoards = () => {
    // Extract unique industries/categories from jobs
    const allCategories = aksharJobs
      .map(job => {
        const cat = job.industry || job.job_category || job.category || 'General';
        // Ensure category is a string
        if (typeof cat === 'string' && cat.trim()) {
          return cat.trim();
        }
        return 'General';
      })
      .filter(cat => cat && cat !== '');
    
    // Get unique categories, ensuring all are strings
    const uniqueCategoriesSet = new Set(allCategories.filter(cat => typeof cat === 'string'));
    const uniqueCategories = ['all', ...Array.from(uniqueCategoriesSet)];
    const jobCategories = uniqueCategories;
    
    // Filter jobs by category
    const filteredJobs = aksharJobs.filter(job => {
      if (jobCategoryFilter === 'all') return true;
      const jobCat = job.industry || job.job_category || job.category || 'General';
      const jobCategory = typeof jobCat === 'string' ? jobCat.toLowerCase().trim() : 'general';
      const filterCategory = typeof jobCategoryFilter === 'string' ? jobCategoryFilter.toLowerCase().trim() : 'all';
      return jobCategory === filterCategory;
    });

    // Map common industry names to user-friendly categories
    const categoryMap = {
      'all': 'All Jobs',
      'it': 'IT Jobs',
      'information technology': 'IT Jobs',
      'software': 'IT Jobs',
      'technology': 'IT Jobs',
      'accounting': 'Accounting',
      'finance': 'Accounting',
      'sales': 'Sales',
      'marketing': 'Sales',
      'business': 'Business',
      'management': 'Business',
      'engineering': 'Engineering',
      'healthcare': 'Healthcare',
      'education': 'Education',
      'general': 'General'
    };

    const getDisplayCategory = (cat) => {
      // Handle null, undefined, or non-string values
      if (!cat || typeof cat !== 'string') {
        return 'General';
      }
      const lowerCat = cat.toLowerCase().trim();
      return categoryMap[lowerCat] || (cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase());
    };

    return (
      <div className="section-card">
        <h2>AksharJobs - Job Board</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          Browse and explore all job postings available on AksharJobs. Filter by category to find relevant opportunities.
        </p>

        {/* Category Filter */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          marginBottom: '1.5rem', 
          alignItems: 'center',
          flexWrap: 'nowrap'
        }}>
          <select
            value={jobCategoryFilter}
            onChange={(e) => setJobCategoryFilter(e.target.value)}
            style={{
              width: '200px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Jobs</option>
            {jobCategories
              .filter(cat => cat !== 'all' && cat && typeof cat === 'string')
              .map(category => (
                <option key={category} value={category}>
                  {getDisplayCategory(category)}
                </option>
              ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} found
          </span>
        </div>

        {jobBoardLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>
              {jobCategoryFilter === 'all' ? 'No jobs found' : `No ${getDisplayCategory(jobCategoryFilter)} jobs found`}
            </p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              {jobCategoryFilter === 'all' ? 'There are currently no job postings on AksharJobs.' : 'Try selecting a different category.'}
            </p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Company</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Job Type</th>
                  <th>Salary Range</th>
                  <th>Posted Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job._id || job.id}>
                    <td style={{ fontWeight: 600, color: '#0f172a' }}>
                      {job.job_title || job.jobTitle || job.title}
                    </td>
                    <td>{job.company_name || job.companyName || job.company}</td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(139, 92, 246, 0.1)',
                        color: '#8b5cf6'
                      }}>
                        {getDisplayCategory(job.industry || job.job_category || 'General')}
                      </span>
                    </td>
                    <td>{job.location || 'N/A'}</td>
                    <td>{job.job_type || job.jobType || 'Full-time'}</td>
                    <td>{job.salary_range || job.salaryRange || 'Not specified'}</td>
                    <td>
                      {job.created_at 
                        ? new Date(job.created_at).toLocaleDateString() 
                        : job.createdAt 
                        ? new Date(job.createdAt).toLocaleDateString() 
                        : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render CV Booklets Generator section
  const renderCvBooklets = () => {
    const handleGenerateBooklet = async () => {
      try {
        setGeneratingBooklet(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(
          buildApiUrl('/api/moringa/cv-booklets/generate'),
          cvBookletForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          alert('CV Booklet generated successfully!');
          setShowCvBookletForm(false);
          setCvBookletForm({
            cohort: '',
            skillSet: '',
            year: '',
            department: '',
            format: 'PDF',
            includePhoto: true,
            includeContact: true,
            photoUploads: [],
            contactInfo: {
              includeEmail: true,
              includePhone: true,
              includeAddress: false,
              includeLinkedIn: false,
              includePortfolio: false
            }
          });
          // Refresh booklets list
          const bookletsResponse = await axios.get(
            buildApiUrl('/api/moringa/cv-booklets'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (bookletsResponse.data?.success) {
            setCvBooklets(bookletsResponse.data.booklets || []);
          }
        } else {
          alert(response.data?.error || 'Failed to generate CV booklet. Please try again.');
        }
      } catch (error) {
        console.error('Failed to generate CV booklet:', error);
        alert('Failed to generate CV booklet. Please try again.');
      } finally {
        setGeneratingBooklet(false);
      }
    };

    return (
      <div className="section-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2>CV Booklets Generator</h2>
            <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px' }}>
              Generate professional CV booklets per cohort or skill set to share with employer partners.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowCvBookletForm(true)}
            style={{ 
              width: '140px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={faFilePdf} />
            <span>Generate Booklet</span>
          </button>
        </div>

        {/* Generated Booklets List */}
        {cvBookletsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : cvBooklets.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>No CV booklets generated yet</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Generate your first CV booklet to share with employer partners.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {cvBooklets.map((booklet) => (
              <div
                key={booklet.id}
                style={{
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}
              >
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.5rem' }}>
                    {booklet.name || `${booklet.cohort} - ${booklet.skillSet}`}
                  </h4>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>
                    Cohort: {booklet.cohort || 'N/A'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>
                    Skill Set: {booklet.skillSet || 'All Skills'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {booklet.studentCount || 0} students included
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ 
                      width: '140px', 
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(
                          buildApiUrl(`/api/moringa/cv-booklets/${booklet.id}/download`),
                          {
                            headers: { Authorization: `Bearer ${token}` }
                          }
                        );
                        
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${booklet.name || 'cv_booklet'}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } else {
                          alert('Failed to download CV booklet. Please try again.');
                        }
                      } catch (error) {
                        console.error('Download error:', error);
                        alert('Failed to download CV booklet. Please try again.');
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>Download</span>
                  </button>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  Generated: {booklet.createdAt ? new Date(booklet.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CV Booklet Generation Form Modal */}
        {showCvBookletForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem 1rem'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCvBookletForm(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '600px',
                padding: '2rem',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Generate CV Booklet</h3>
                <button
                  type="button"
                  onClick={() => setShowCvBookletForm(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>
              {/* Mode Selection */}
              <div style={{
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginBottom: '1rem'
              }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
                  Select Method:
                </label>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <label style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: bookletMode === 'generate' ? '2px solid #f97316' : '1px solid #d1d5db',
                    background: bookletMode === 'generate' ? '#fff7ed' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <input
                      type="radio"
                      name="bookletMode"
                      value="generate"
                      checked={bookletMode === 'generate'}
                      onChange={(e) => {
                        setBookletMode('generate');
                        setUploadedBookletFile(null);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>Generate Booklet</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Auto-generate from student data</div>
                    </div>
                  </label>
                  <label style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: bookletMode === 'upload' ? '2px solid #f97316' : '1px solid #d1d5db',
                    background: bookletMode === 'upload' ? '#fff7ed' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <input
                      type="radio"
                      name="bookletMode"
                      value="upload"
                      checked={bookletMode === 'upload'}
                      onChange={(e) => {
                        setBookletMode('upload');
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>Upload Existing PDF</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Upload your pre-made booklet</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Upload Mode - Simple Upload Form */}
              {bookletMode === 'upload' ? (
                <>
                  <div style={{
                    padding: '1.5rem',
                    background: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #fde68a',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', marginBottom: '0.75rem' }}>
                      📄 Upload Your CV Booklet PDF
                    </h4>
                    <p style={{ fontSize: '0.75rem', color: '#78350f', marginBottom: '1rem', lineHeight: '1.6' }}>
                      Upload a pre-made CV booklet PDF that your university has already created. This is the easiest option if you have the booklet ready.
                    </p>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.type !== 'application/pdf') {
                            alert('Please upload a PDF file only.');
                            e.target.value = '';
                            return;
                          }
                          if (file.size > 50 * 1024 * 1024) { // 50MB limit
                            alert('File size should be less than 50MB.');
                            e.target.value = '';
                            return;
                          }
                          setUploadedBookletFile(file);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        borderRadius: '6px',
                        border: '1px solid #fde68a',
                        fontSize: '0.875rem',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    />
                    {uploadedBookletFile && (
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem',
                        background: '#fef9c3',
                        borderRadius: '6px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontSize: '0.875rem', color: '#92400e' }}>
                          ✓ {uploadedBookletFile.name} ({(uploadedBookletFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedBookletFile(null);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: '#fef9c3',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      color: '#713f12'
                    }}>
                      <strong>💡 Tips:</strong>
                      <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                        <li>Ensure the PDF is well-formatted and professional</li>
                        <li>Include all relevant student information</li>
                        <li>Maximum file size: 50MB</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowCvBookletForm(false);
                        setBookletMode('generate');
                        setUploadedBookletFile(null);
                      }}
                      style={{ 
                        width: '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={async () => {
                        if (!uploadedBookletFile) {
                          alert('Please select a PDF file to upload.');
                          return;
                        }
                        try {
                          setGeneratingBooklet(true);
                          const token = localStorage.getItem('token');
                          const formData = new FormData();
                          formData.append('bookletPdf', uploadedBookletFile);
                          formData.append('cohort', cvBookletForm.cohort || 'Uploaded Booklet');
                          formData.append('format', 'PDF');
                          formData.append('mode', 'upload');
                          
                          const response = await axios.post(
                            buildApiUrl('/api/moringa/cv-booklets/upload'),
                            formData,
                            { 
                              headers: { 
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                              } 
                            }
                          );
                          
                          if (response.data?.success) {
                            alert('CV Booklet uploaded successfully!');
                            setShowCvBookletForm(false);
                            setUploadedBookletFile(null);
                            setBookletMode('generate');
                            // Refresh booklets list
                            const bookletsResponse = await axios.get(
                              buildApiUrl('/api/moringa/cv-booklets'),
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (bookletsResponse.data?.success) {
                              setCvBooklets(bookletsResponse.data.booklets || []);
                            }
                          } else {
                            alert(response.data?.error || 'Failed to upload CV booklet. Please try again.');
                          }
                        } catch (error) {
                          console.error('Failed to upload CV booklet:', error);
                          alert(error.response?.data?.error || 'Failed to upload CV booklet. Please try again.');
                        } finally {
                          setGeneratingBooklet(false);
                        }
                      }}
                      disabled={generatingBooklet || !uploadedBookletFile}
                      style={{ 
                        width: '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span>{generatingBooklet ? 'Uploading...' : 'Upload Booklet'}</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
              {/* Generate Mode - Full Form */}
                  {/* Instructions Section */}
                  <div style={{
                    padding: '1rem',
                    background: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #bae6fd',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0c4a6e', marginBottom: '0.5rem' }}>
                      📖 How to Use This Form
                    </h4>
                    <ul style={{ fontSize: '0.75rem', color: '#075985', margin: 0, paddingLeft: '1.5rem', lineHeight: '1.6' }}>
                      <li><strong>Cohort/Year:</strong> Enter the cohort identifier (e.g., "2024", "Cohort A", "Fall 2024")</li>
                      <li><strong>Skill Set/Department:</strong> Specify the department or skill set (e.g., "Software Engineering", "Data Science", "Business Administration")</li>
                      <li><strong>Year:</strong> Enter the graduation year (e.g., "2024")</li>
                      <li><strong>Photos & Contact:</strong> Toggle options below to customize what appears in the booklet</li>
                    </ul>
                  </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Cohort / Year <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={cvBookletForm.cohort}
                    onChange={(e) => setCvBookletForm(prev => ({ ...prev, cohort: e.target.value }))}
                    placeholder="e.g. 2024, Cohort A"
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    Skill Set / Department <span style={{ color: '#6b7280', fontWeight: 400, fontSize: '0.75rem' }}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={cvBookletForm.skillSet}
                    onChange={(e) => setCvBookletForm(prev => ({ ...prev, skillSet: e.target.value }))}
                    placeholder="e.g. Software Engineering, Data Science"
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Year
                    </label>
                    <input
                      type="text"
                      value={cvBookletForm.year}
                      onChange={(e) => setCvBookletForm(prev => ({ ...prev, year: e.target.value }))}
                      placeholder="e.g. 2024"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Format
                    </label>
                    <select
                      value={cvBookletForm.format}
                      onChange={(e) => setCvBookletForm(prev => ({ ...prev, format: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="PDF">PDF</option>
                      <option value="DOCX">DOCX</option>
                      <option value="EXCEL">Excel</option>
                    </select>
                  </div>
                </div>
                {/* Sample/Preview Section */}
                <div style={{
                  padding: '1rem',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid #bae6fd',
                  marginTop: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: 600, color: '#0c4a6e', fontSize: '0.875rem' }}>📋 Sample CV Booklet Preview</span>
                    <button
                      type="button"
                      onClick={() => setShowSamplePreview(!showSamplePreview)}
                      style={{
                        background: 'transparent',
                        border: '1px solid #0284c7',
                        color: '#0284c7',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                    >
                      {showSamplePreview ? 'Hide' : 'Show'} Sample
                    </button>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#075985', marginBottom: '0.5rem' }}>
                    💡 <strong>Tip:</strong> The booklet will include student information based on your selections below. Students' data (photos, contact info) will be pulled from their profiles in the system.
                  </p>
                  {showSamplePreview && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '1rem',
                      background: 'white',
                      borderRadius: '6px',
                      border: '1px dashed #7dd3fc'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#0c4a6e', lineHeight: '1.6' }}>
                        <p><strong>Sample CV Booklet Structure:</strong></p>
                        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                          <li>Cover page with cohort/department info</li>
                          <li>Student photo (if enabled) + Name</li>
                          <li>Email address (if enabled)</li>
                          <li>Phone number (if enabled)</li>
                          <li>Address (if enabled)</li>
                          <li>LinkedIn profile (if enabled)</li>
                          <li>Portfolio/Website (if enabled)</li>
                          <li>Skills, Education, Experience summary</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>

                {/* Photo Upload Section - Only in Generate Mode */}
                {bookletMode === 'generate' && (
                <div style={{
                  padding: '1rem',
                  background: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px solid #fde68a',
                  marginTop: '0.5rem'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginBottom: '0.75rem', fontWeight: 600, color: '#92400e' }}>
                    <input
                      type="checkbox"
                      checked={cvBookletForm.includePhoto}
                      onChange={(e) => setCvBookletForm(prev => ({ ...prev, includePhoto: e.target.checked }))}
                    />
                    📸 Include Student Photos
                  </label>
                  {cvBookletForm.includePhoto && (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px' }}>
                      <p style={{ fontSize: '0.75rem', color: '#78350f', marginBottom: '0.75rem' }}>
                        <strong>How it works:</strong> Student photos are automatically pulled from their profile images. If a student doesn't have a profile photo, their entry will show a placeholder or initials.
                      </p>
                      <div style={{
                        padding: '0.75rem',
                        background: '#fef9c3',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: '#713f12'
                      }}>
                        <strong>📝 Note:</strong> To update student photos, go to the Students page and update individual student profiles. Photos will be automatically included in the booklet based on the cohort/department you select.
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Contact Information Section - Only in Generate Mode */}
                {bookletMode === 'generate' && (
                <div style={{
                  padding: '1rem',
                  background: '#ecfdf5',
                  borderRadius: '8px',
                  border: '1px solid #a7f3d0',
                  marginTop: '0.5rem'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginBottom: '0.75rem', fontWeight: 600, color: '#065f46' }}>
                    <input
                      type="checkbox"
                      checked={cvBookletForm.includeContact}
                      onChange={(e) => setCvBookletForm(prev => ({ ...prev, includeContact: e.target.checked }))}
                    />
                    📞 Include Contact Information
                  </label>
                  {cvBookletForm.includeContact && (
                    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px' }}>
                      <p style={{ fontSize: '0.75rem', color: '#064e3b', marginBottom: '0.75rem', fontWeight: 600 }}>
                        Select which contact details to include:
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={cvBookletForm.contactInfo.includeEmail}
                            onChange={(e) => setCvBookletForm(prev => ({
                              ...prev,
                              contactInfo: { ...prev.contactInfo, includeEmail: e.target.checked }
                            }))}
                          />
                          Email Address
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={cvBookletForm.contactInfo.includePhone}
                            onChange={(e) => setCvBookletForm(prev => ({
                              ...prev,
                              contactInfo: { ...prev.contactInfo, includePhone: e.target.checked }
                            }))}
                          />
                          Phone Number
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={cvBookletForm.contactInfo.includeAddress}
                            onChange={(e) => setCvBookletForm(prev => ({
                              ...prev,
                              contactInfo: { ...prev.contactInfo, includeAddress: e.target.checked }
                            }))}
                          />
                          Physical Address
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={cvBookletForm.contactInfo.includeLinkedIn}
                            onChange={(e) => setCvBookletForm(prev => ({
                              ...prev,
                              contactInfo: { ...prev.contactInfo, includeLinkedIn: e.target.checked }
                            }))}
                          />
                          LinkedIn Profile
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={cvBookletForm.contactInfo.includePortfolio}
                            onChange={(e) => setCvBookletForm(prev => ({
                              ...prev,
                              contactInfo: { ...prev.contactInfo, includePortfolio: e.target.checked }
                            }))}
                          />
                          Portfolio/Website
                        </label>
                      </div>
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem',
                        background: '#d1fae5',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: '#065f46'
                      }}>
                        <strong>📝 Note:</strong> Contact information is automatically pulled from each student's profile. Ensure students have completed their profiles with the relevant contact details you want to include.
                      </div>
                    </div>
                  )}
                </div>
                )}
                
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCvBookletForm(false);
                      setBookletMode('generate');
                      setUploadedBookletFile(null);
                    }}
                    style={{ 
                      width: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleGenerateBooklet}
                    disabled={generatingBooklet || !cvBookletForm.cohort}
                    style={{ 
                      width: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span>{generatingBooklet ? 'Generating...' : 'Generate Booklet'}</span>
                  </button>
                </div>
                </>
              )}
              </div>
            </div>
          )}
      </div>
    );
  };

  // Render Employer Partner Events section
  const renderEmployerEvents = () => {
    const handleCreateEvent = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          buildApiUrl('/api/moringa/employer-events'),
          employerEventForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert('Employer event created successfully!');
          
          // Refresh events list
          const eventsResponse = await axios.get(
            buildApiUrl('/api/moringa/employer-events'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (eventsResponse.data?.success) {
            setEmployerEvents(eventsResponse.data.events || []);
          }
          
          setShowEmployerEventForm(false);
          setEmployerEventForm({
            title: '',
            description: '',
            date: '',
            time: '',
            location: '',
            partnerCompany: '',
            eventType: 'Networking',
            maxAttendees: '',
            contactEmail: '',
            contactPhone: ''
          });
        } else {
          alert(response.data?.error || 'Failed to create event. Please try again.');
        }
      } catch (error) {
        console.error('Failed to create employer event:', error);
        alert(error.response?.data?.error || 'Failed to create event. Please try again.');
      }
    };

    const handleUpdateEvent = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          buildApiUrl(`/api/moringa/employer-events/${editingEmployerEvent.id}`),
          employerEventForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert('Event updated successfully!');
          
          const eventsResponse = await axios.get(
            buildApiUrl('/api/moringa/employer-events'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (eventsResponse.data?.success) {
            setEmployerEvents(eventsResponse.data.events || []);
          }
          
          setEditingEmployerEvent(null);
          setShowEmployerEventForm(false);
          setEmployerEventForm({
            title: '',
            description: '',
            date: '',
            time: '',
            location: '',
            partnerCompany: '',
            eventType: 'Networking',
            maxAttendees: '',
            contactEmail: '',
            contactPhone: ''
          });
        } else {
          alert(response.data?.error || 'Failed to update event. Please try again.');
        }
      } catch (error) {
        console.error('Failed to update event:', error);
        alert(error.response?.data?.error || 'Failed to update event. Please try again.');
      }
    };

    const handleDeleteEvent = async (eventId) => {
      if (!window.confirm('Are you sure you want to delete this event?')) return;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          buildApiUrl(`/api/moringa/employer-events/${eventId}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert('Event deleted successfully!');
          
          const eventsResponse = await axios.get(
            buildApiUrl('/api/moringa/employer-events'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (eventsResponse.data?.success) {
            setEmployerEvents(eventsResponse.data.events || []);
          }
        } else {
          alert(response.data?.error || 'Failed to delete event. Please try again.');
        }
      } catch (error) {
        console.error('Failed to delete event:', error);
        alert(error.response?.data?.error || 'Failed to delete event. Please try again.');
      }
    };

    const handleEditEvent = (event) => {
      setEditingEmployerEvent(event);
      setEmployerEventForm({
        title: event.title || '',
        description: event.description || '',
        date: event.date ? event.date.split('T')[0] : '',
        time: event.time || '',
        location: event.location || '',
        partnerCompany: event.partnerCompany || '',
        eventType: event.eventType || 'Networking',
        maxAttendees: event.maxAttendees || '',
        contactEmail: event.contactEmail || '',
        contactPhone: event.contactPhone || ''
      });
      setShowEmployerEventForm(true);
    };

    const handleRegisterForEvent = async (eventId) => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          buildApiUrl(`/api/moringa/employer-events/${eventId}/register`),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert('Successfully registered for the event!');
          
          // Refresh events list
          const eventsResponse = await axios.get(
            buildApiUrl('/api/moringa/employer-events'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (eventsResponse.data?.success) {
            setEmployerEvents(eventsResponse.data.events || []);
          }
        } else {
          alert(response.data?.error || 'Failed to register for event. Please try again.');
        }
      } catch (error) {
        console.error('Failed to register for event:', error);
        alert(error.response?.data?.error || 'Failed to register for event. Please try again.');
      }
    };

    const handleUnregisterFromEvent = async (eventId) => {
      if (!window.confirm('Are you sure you want to cancel your registration?')) return;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          buildApiUrl(`/api/moringa/employer-events/${eventId}/register`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert('Registration cancelled successfully.');
          
          // Refresh events list
          const eventsResponse = await axios.get(
            buildApiUrl('/api/moringa/employer-events'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (eventsResponse.data?.success) {
            setEmployerEvents(eventsResponse.data.events || []);
          }
        } else {
          alert(response.data?.error || 'Failed to cancel registration. Please try again.');
        }
      } catch (error) {
        console.error('Failed to cancel registration:', error);
        alert(error.response?.data?.error || 'Failed to cancel registration. Please try again.');
      }
    };

    const handleViewRegistrations = async (event) => {
      try {
        setEventRegistrationsLoading(true);
        setSelectedAttendees([]);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl(`/api/moringa/employer-events/${event.id}/registrations`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          setSelectedEventRegistrations({
            event: event,
            registrations: response.data.registrations || [],
            total: response.data.total || 0
          });
          setShowEventRegistrations(true);
        } else {
          alert(response.data?.error || 'Failed to fetch registrations.');
        }
      } catch (error) {
        console.error('Failed to fetch registrations:', error);
        alert(error.response?.data?.error || 'Failed to fetch registrations.');
      } finally {
        setEventRegistrationsLoading(false);
      }
    };

    const handleMarkAttendance = async () => {
      if (selectedAttendees.length === 0) {
        alert('Please select at least one student to mark as attended.');
        return;
      }

      try {
        setMarkingAttendance(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(
          buildApiUrl(`/api/moringa/employer-events/${selectedEventRegistrations.event.id}/attendance`),
          {
            studentIds: selectedAttendees,
            attended: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert(`Successfully marked ${response.data.message.split(' ')[4]} student(s) as attended.`);
          
          // Refresh registrations
          await handleViewRegistrations(selectedEventRegistrations.event);
          setSelectedAttendees([]);
        } else {
          alert(response.data?.error || 'Failed to mark attendance.');
        }
      } catch (error) {
        console.error('Failed to mark attendance:', error);
        alert(error.response?.data?.error || 'Failed to mark attendance.');
      } finally {
        setMarkingAttendance(false);
      }
    };

    const eventTypes = ['Networking', 'Career Fair', 'Company Visit', 'Workshop', 'Recruitment Drive', 'Info Session'];

    return (
      <div className="section-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2>Employer Partner Events</h2>
            <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px' }}>
              Manage events organized in partnership with employer partners to connect students with opportunities.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setEditingEmployerEvent(null);
              setEmployerEventForm({
                title: '',
                description: '',
                date: '',
                time: '',
                location: '',
                partnerCompany: '',
                eventType: 'Networking',
                maxAttendees: '',
                contactEmail: '',
                contactPhone: ''
              });
              setShowEmployerEventForm(true);
            }}
            style={{ 
              width: '140px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={faCalendar} />
            <span>Create Event</span>
          </button>
        </div>

        {employerEventsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : employerEvents.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faHandshake} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b' }}>No employer partner events yet</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {employerEvents.map((event) => {
              const eventDate = event.date ? new Date(event.date) : null;
              const now = new Date();
              const isUpcoming = eventDate && eventDate >= now;

              return (
                <div
                  key={event.id}
                  style={{
                    padding: '1.25rem 1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: isUpcoming ? '#ffffff' : '#f9fafb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1.5rem'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                        {event.title}
                      </h3>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(59, 130, 246, 0.1)',
                        color: '#3b82f6'
                      }}>
                        {event.eventType}
                      </span>
                      {event.partnerCompany && (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: 'rgba(249, 115, 22, 0.1)',
                          color: '#f97316'
                        }}>
                          {event.partnerCompany}
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                        {event.description.length > 150 ? `${event.description.substring(0, 150)}...` : event.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#64748b' }}>
                      {eventDate && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faCalendar} />
                          <span>{eventDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          {event.time && <span>at {event.time}</span>}
                        </div>
                      )}
                      {event.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          <span>{event.location}</span>
                        </div>
                      )}
                      {event.maxAttendees > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faUsers} />
                          <span>{event.attendees || 0} / {event.maxAttendees} attendees</span>
                          {event.isFull && (
                            <span style={{ 
                              padding: '0.125rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              background: '#fee2e2',
                              color: '#dc2626'
                            }}>
                              FULL
                            </span>
                          )}
                        </div>
                      )}
                      {event.isRegistered && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#059669' }}>
                          <FontAwesomeIcon icon={faCheckCircle} />
                          <span style={{ fontWeight: 600 }}>Registered</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {event.isRegistered ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleUnregisterFromEvent(event.id)}
                        style={{ 
                          width: '140px',
                          fontSize: '0.85rem', 
                          padding: '0.5rem 0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faX} />
                        <span>Cancel Registration</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleRegisterForEvent(event.id)}
                        disabled={event.isFull || !isUpcoming}
                        style={{ 
                          width: '140px',
                          fontSize: '0.85rem', 
                          padding: '0.5rem 0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          opacity: (event.isFull || !isUpcoming) ? 0.5 : 1,
                          cursor: (event.isFull || !isUpcoming) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <FontAwesomeIcon icon={faUserPlus} />
                        <span>Register</span>
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleViewRegistrations(event)}
                      style={{ 
                        width: '140px', 
                        fontSize: '0.85rem', 
                        padding: '0.5rem 0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FontAwesomeIcon icon={faUsers} />
                      <span>View ({event.attendees || 0})</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleEditEvent(event)}
                      style={{ 
                        width: '140px', 
                        fontSize: '0.85rem', 
                        padding: '0.5rem 0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleDeleteEvent(event.id)}
                      style={{ 
                        width: '140px',
                        fontSize: '0.85rem', 
                        padding: '0.5rem 0.75rem',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '1px solid #fecaca',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Employer Event Form Modal */}
        {showEmployerEventForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem 1rem'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEmployerEventForm(false);
                setEditingEmployerEvent(null);
              }
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              <div style={{ 
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                  {editingEmployerEvent ? 'Edit Employer Event' : 'Create Employer Partner Event'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowEmployerEventForm(false);
                    setEditingEmployerEvent(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ 
                padding: '1.5rem',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Event Title *
                    </label>
                    <input
                      type="text"
                      value={employerEventForm.title}
                      onChange={(e) => setEmployerEventForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g. Tech Career Networking Night"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Partner Company *
                    </label>
                    <input
                      type="text"
                      value={employerEventForm.partnerCompany}
                      onChange={(e) => setEmployerEventForm(prev => ({ ...prev, partnerCompany: e.target.value }))}
                      placeholder="e.g. Microsoft, Google"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Description
                    </label>
                    <textarea
                      value={employerEventForm.description}
                      onChange={(e) => setEmployerEventForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Event description and details..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Date *
                      </label>
                      <input
                        type="date"
                        value={employerEventForm.date}
                        onChange={(e) => setEmployerEventForm(prev => ({ ...prev, date: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Time
                      </label>
                      <input
                        type="time"
                        value={employerEventForm.time}
                        onChange={(e) => setEmployerEventForm(prev => ({ ...prev, time: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Event Type
                      </label>
                      <select
                        value={employerEventForm.eventType}
                        onChange={(e) => setEmployerEventForm(prev => ({ ...prev, eventType: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      >
                        {eventTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Max Attendees
                      </label>
                      <input
                        type="number"
                        value={employerEventForm.maxAttendees}
                        onChange={(e) => setEmployerEventForm(prev => ({ ...prev, maxAttendees: e.target.value }))}
                        placeholder="0 for unlimited"
                        min="0"
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Location
                    </label>
                    <input
                      type="text"
                      value={employerEventForm.location}
                      onChange={(e) => setEmployerEventForm(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g. Main Auditorium, Building A"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={employerEventForm.contactEmail}
                        onChange={(e) => setEmployerEventForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                        placeholder="contact@company.com"
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        value={employerEventForm.contactPhone}
                        onChange={(e) => setEmployerEventForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                        placeholder="+254 700 000 000"
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ 
                padding: '1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
                flexShrink: 0
              }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEmployerEventForm(false);
                    setEditingEmployerEvent(null);
                  }}
                  style={{ 
                    width: '140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={editingEmployerEvent ? handleUpdateEvent : handleCreateEvent}
                  disabled={!employerEventForm.title || !employerEventForm.date || !employerEventForm.partnerCompany}
                  style={{ 
                    width: '140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <FontAwesomeIcon icon={faCalendar} />
                  <span>{editingEmployerEvent ? 'Update Event' : 'Create Event'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Event Registrations Modal */}
        {showEventRegistrations && selectedEventRegistrations && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem 1rem'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEventRegistrations(false);
                setSelectedEventRegistrations(null);
              }
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              <div style={{ 
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                    Event Registrations
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
                    {selectedEventRegistrations.event.title} • {selectedEventRegistrations.total} registered
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventRegistrations(false);
                    setSelectedEventRegistrations(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ 
                padding: '1.5rem',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0
              }}>
                {eventRegistrationsLoading ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <ThemedLoadingSpinner theme="university" />
                  </div>
                ) : selectedEventRegistrations.registrations.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <FontAwesomeIcon icon={faUsers} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
                    <p style={{ fontSize: '1rem', color: '#64748b' }}>No registrations yet</p>
                  </div>
                ) : (
                  <>
                    {selectedAttendees.length > 0 && (
                      <div style={{
                        padding: '0.75rem 1rem',
                        background: '#eff6ff',
                        borderRadius: '8px',
                        border: '1px solid #bfdbfe',
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: 600 }}>
                          {selectedAttendees.length} student(s) selected
                        </span>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleMarkAttendance}
                          disabled={markingAttendance}
                          style={{
                            width: '140px',
                            fontSize: '0.85rem',
                            padding: '0.5rem 0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          {markingAttendance ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin />
                              <span>Marking...</span>
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faCheckCircle} />
                              <span>Mark Attended</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {selectedEventRegistrations.registrations.map((registration) => (
                        <div
                          key={registration.registrationId}
                          style={{
                            padding: '1rem',
                            borderRadius: '8px',
                            border: selectedAttendees.includes(registration.studentId) ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                            background: selectedAttendees.includes(registration.studentId) ? '#eff6ff' : '#ffffff',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (!registration.attended) {
                              setSelectedAttendees(prev => 
                                prev.includes(registration.studentId)
                                  ? prev.filter(id => id !== registration.studentId)
                                  : [...prev, registration.studentId]
                              );
                            }
                          }}
                        >
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            {!registration.attended && (
                              <input
                                type="checkbox"
                                checked={selectedAttendees.includes(registration.studentId)}
                                onChange={() => {
                                  setSelectedAttendees(prev => 
                                    prev.includes(registration.studentId)
                                      ? prev.filter(id => id !== registration.studentId)
                                      : [...prev, registration.studentId]
                                  );
                                }}
                                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                                {registration.name}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>
                                {registration.email}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                {registration.department && `${registration.department}`}
                                {registration.department && registration.course && ' • '}
                                {registration.course && `${registration.course}`}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                                Registered: {new Date(registration.registeredAt).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {registration.attended && (
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                background: '#d1fae5',
                                color: '#059669'
                              }}>
                                Attended
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Analytics section (similar to University Dashboard)
  const renderAnalytics = () => {
    const stats = dashboardData.stats || {};
    const total = stats.totalStudents || 0;
    const placed = stats.studentsPlaced || 0;
    const placementRate = total > 0 ? Math.round((placed / total) * 100) : 0;

    // Chart data - Students by Department
    const studentsByDeptData = {
      labels: ['Computer Science', 'Business', 'Engineering', 'Arts & Design', 'Other'],
      datasets: [{
        label: 'Students',
        data: [35, 28, 22, 10, 5],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(107, 114, 128, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(249, 115, 22)',
          'rgb(16, 185, 129)',
          'rgb(139, 92, 246)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 2
      }]
    };

    // Chart data - Students by Gender
    const studentsByGenderData = {
      labels: ['Male', 'Female', 'Other'],
      datasets: [{
        label: 'Students',
        data: [55, 42, 3],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(107, 114, 128, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(236, 72, 153)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 2
      }]
    };

    // Chart data - Placement Rate Over Time (Last 6 Months)
    const placementTrendData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [{
        label: 'Placement Rate (%)',
        data: [65, 68, 72, 70, 75, placementRate || 78],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgb(16, 185, 129)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }]
    };

    // Chart data - Job Applications by Status
    const applicationsByStatusData = {
      labels: ['Pending', 'Shortlisted', 'Interview', 'Rejected', 'Accepted'],
      datasets: [{
        label: 'Applications',
        data: [45, 28, 18, 12, 22],
        backgroundColor: [
          'rgba(251, 191, 36, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(16, 185, 129, 0.8)'
        ],
        borderColor: [
          'rgb(251, 191, 36)',
          'rgb(59, 130, 246)',
          'rgb(139, 92, 246)',
          'rgb(239, 68, 68)',
          'rgb(16, 185, 129)'
        ],
        borderWidth: 2
      }]
    };

    // Chart data - Coaching Sessions Over Time
    const coachingSessionsData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [{
        label: 'Coaching Sessions',
        data: [12, 18, 15, 22, 20, stats.coachingSessions || 25],
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2
      }]
    };

    // Chart data - Students by Graduation Year
    const studentsByYearData = {
      labels: ['2024', '2025', '2026', '2027'],
      datasets: [{
        label: 'Students',
        data: [30, 35, 28, 17],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(139, 92, 246, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(249, 115, 22)',
          'rgb(16, 185, 129)',
          'rgb(139, 92, 246)'
        ],
        borderWidth: 2
      }]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          titleFont: {
            size: 14
          },
          bodyFont: {
            size: 12
          }
        }
      }
    };

    const lineChartOptions = {
      ...chartOptions,
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };

    return (
      <div className="section-card">
        <h2>Analytics</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          Comprehensive analytics for Moringa Career Services Platform.
        </p>

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Total Students
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{total}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Placement Rate
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{placementRate}%</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Coaching Sessions
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#3b82f6' }}>{stats.coachingSessions || 0}</div>
          </div>
        </div>

        {/* Charts Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
          {/* Students by Department - Doughnut Chart */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Students by Department
            </h3>
            <div style={{ height: '300px', position: 'relative' }}>
              <Doughnut data={studentsByDeptData} options={chartOptions} />
            </div>
          </div>

          {/* Students by Gender - Doughnut Chart */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Students by Gender
            </h3>
            <div style={{ height: '300px', position: 'relative' }}>
              <Doughnut data={studentsByGenderData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Placement Trends - Line Chart */}
        <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
            Placement Rate Trend (Last 6 Months)
          </h3>
          <div style={{ height: '300px', position: 'relative' }}>
            <Line data={placementTrendData} options={lineChartOptions} />
          </div>
        </div>

        {/* Bottom Charts Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
          {/* Job Applications by Status - Bar Chart */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Job Applications by Status
            </h3>
            <div style={{ height: '300px', position: 'relative' }}>
              <Bar data={applicationsByStatusData} options={chartOptions} />
            </div>
          </div>

          {/* Coaching Sessions Over Time - Bar Chart */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Coaching Sessions Over Time
            </h3>
            <div style={{ height: '300px', position: 'relative' }}>
              <Bar data={coachingSessionsData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Students by Graduation Year - Bar Chart */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
            Students by Graduation Year
          </h3>
          <div style={{ height: '300px', position: 'relative' }}>
            <Bar data={studentsByYearData} options={chartOptions} />
          </div>
        </div>
      </div>
    );
  };

  // Handle bulk upload file selection
  const handleBulkUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBulkUploadFile(file);
    }
  };

  // Handle bulk upload submission
  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      alert('Please select a file to upload');
      return;
    }

    setBulkUploadLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', bulkUploadFile);
      formData.append('source', 'Moringa University');
      formData.append('userType', 'job_seeker');

      const response = await axios.post(
        buildApiUrl('/api/bulk-import/upload'),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.batch_id) {
        // Validate the uploaded data
        const validateResponse = await axios.post(
          buildApiUrl(`/api/bulk-import/validate/${response.data.batch_id}`),
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        setBulkUploadResults(validateResponse.data);
        
        // Auto-process if validation passes
        if (validateResponse.data.valid_count > 0) {
          const processResponse = await axios.post(
            buildApiUrl(`/api/bulk-import/process/${response.data.batch_id}`),
            {
              skipDuplicates: true,
              sendVerification: true,
              autoVerify: false,
              sendWelcomeEmail: true
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          setBulkUploadResults(prev => ({
            ...prev,
            processed: true,
            importStatus: processResponse.data
          }));
          
          alert(`✅ Successfully uploaded ${validateResponse.data.valid_count} students!`);
          
          // Refresh students list
          const studentsResponse = await axios.get(
            buildApiUrl('/api/moringa/students'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setStudents(studentsResponse.data.students || []);
          
          // Reset form
          setBulkUploadFile(null);
          setShowBulkUpload(false);
          const fileInput = document.getElementById('bulk-upload-file');
          if (fileInput) fileInput.value = '';
        }
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      alert('❌ Upload failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // Convert 24-hour time to 12-hour format with AM/PM
  const convert24to12 = (time24) => {
    if (!time24) return { hour: '12', minute: '00', ampm: 'PM' };
    
    try {
      const [hours, minutes] = time24.split(':');
      const hour24 = parseInt(hours, 10);
      const mins = minutes || '00';
      
      if (hour24 === 0) {
        return { hour: '12', minute: mins, ampm: 'AM' };
      } else if (hour24 === 12) {
        return { hour: '12', minute: mins, ampm: 'PM' };
      } else if (hour24 < 12) {
        return { hour: String(hour24), minute: mins, ampm: 'AM' };
      } else {
        return { hour: String(hour24 - 12), minute: mins, ampm: 'PM' };
      }
    } catch (error) {
      return { hour: '12', minute: '00', ampm: 'PM' };
    }
  };

  // Convert 12-hour format to 24-hour format for storage
  const convert12to24 = (hour, minute, ampm) => {
    let hour24 = parseInt(hour, 10);
    const mins = minute || '00';
    
    if (ampm === 'AM') {
      if (hour24 === 12) {
        hour24 = 0;
      }
    } else { // PM
      if (hour24 !== 12) {
        hour24 += 12;
      }
    }
    
    return `${String(hour24).padStart(2, '0')}:${mins.padStart(2, '0')}`;
  };

  // Format time to AM/PM format for display
  const formatTimeToAMPM = (timeString) => {
    if (!timeString) return '';
    
    // Handle different time formats
    try {
      // If already in HH:MM format (24-hour)
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        const hour24 = parseInt(hours, 10);
        const mins = minutes || '00';
        
        if (hour24 === 0) {
          return `12:${mins} AM`;
        } else if (hour24 === 12) {
          return `12:${mins} PM`;
        } else if (hour24 < 12) {
          return `${hour24}:${mins} AM`;
        } else {
          return `${hour24 - 12}:${mins} PM`;
        }
      }
      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  // Download template
  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        buildApiUrl('/api/bulk-import/template?type=job_seeker'),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'moringa_students_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Template download error:', error);
      alert('Failed to download template');
    }
  };

  // Render Students section
  const renderStudents = () => {
    // Get unique departments for filter dropdown
    const departments = [...new Set(students.map(s => s.department || 'Not specified').filter(d => d !== 'Not specified'))].sort();
    
    // Filter students based on search, gender, and department
    let filteredStudents = students.filter(student => {
      // Search filter
      if (studentSearch && !student.name?.toLowerCase().includes(studentSearch.toLowerCase()) && 
          !student.email?.toLowerCase().includes(studentSearch.toLowerCase()) &&
          !student.course?.toLowerCase().includes(studentSearch.toLowerCase())) {
        return false;
      }
      
      // Gender filter
      if (studentGenderFilter !== 'all') {
        const studentGender = (student.gender || '').toLowerCase();
        if (studentGenderFilter === 'male' && !['male', 'm'].includes(studentGender)) {
          return false;
        }
        if (studentGenderFilter === 'female' && !['female', 'f'].includes(studentGender)) {
          return false;
        }
      }
      
      // Department filter
      if (studentDeptFilter !== 'all' && student.department !== studentDeptFilter) {
        return false;
      }
      
      return true;
    });
    
    // Separate by gender
    const maleStudents = filteredStudents.filter(s => {
      const gender = (s.gender || '').toLowerCase();
      return ['male', 'm'].includes(gender);
    });
    
    const femaleStudents = filteredStudents.filter(s => {
      const gender = (s.gender || '').toLowerCase();
      return ['female', 'f'].includes(gender);
    });
    
    const otherStudents = filteredStudents.filter(s => {
      const gender = (s.gender || '').toLowerCase();
      return !['male', 'm', 'female', 'f'].includes(gender);
    });
    
    const renderStudentTable = (studentList, title, iconColor) => {
      if (studentList.length === 0) return null;
      
      return (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem',
            paddingBottom: '0.75rem',
            borderBottom: `2px solid ${iconColor}`
          }}>
            <FontAwesomeIcon icon={faUsers} style={{ color: iconColor, fontSize: '1.25rem' }} />
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1e293b' }}>
              {title} ({studentList.length})
            </h3>
          </div>
          
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Name</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Email</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Phone</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Gender</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Department</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Course/Program</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Year</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Cohort</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {studentList.map((student, index) => (
                  <tr 
                    key={student.id || index}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: 500, color: '#1e293b' }}>
                      {student.name || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {student.email || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {student.phone || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {student.gender || 'Not specified'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {student.department || 'Not specified'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {student.course || 'Not specified'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {student.year || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {student.cohort || 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(16, 185, 129, 0.1)',
                        color: '#10b981'
                      }}>
                        {student.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    };
    
    return (
      <div className="section-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2>Students</h2>
            <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px' }}>
              View and manage all Moringa School students and graduates. Students are organized by gender.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={downloadTemplate}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                color: '#374151',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '180px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
              }}
            >
              <FontAwesomeIcon icon={faDownload} style={{ fontSize: '0.75rem', flexShrink: 0 }} />
              <span>Download Template</span>
            </button>
            <button
              onClick={() => setShowBulkUpload(!showBulkUpload)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                width: '200px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.3)';
              }}
            >
              <FontAwesomeIcon icon={faUpload} style={{ fontSize: '0.75rem', flexShrink: 0 }} />
              <span>Bulk Upload Students</span>
            </button>
          </div>
        </div>

        {/* Bulk Upload Section */}
        {showBulkUpload && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1.5rem',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '2px dashed #e2e8f0'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: 600 }}>
              Bulk Upload Students
            </h3>
            <p style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.875rem' }}>
              Upload a CSV or Excel file with student information. Download the template to see the required format.
            </p>
            
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <label
                htmlFor="bulk-upload-file"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1rem',
                  background: 'white',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#f97316';
                  e.currentTarget.style.color = '#f97316';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = 'inherit';
                }}
              >
                <FontAwesomeIcon icon={faUpload} />
                {bulkUploadFile ? bulkUploadFile.name : 'Choose File'}
              </label>
              <input
                id="bulk-upload-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleBulkUploadFile}
                style={{ display: 'none' }}
              />
              
              {bulkUploadFile && (
                <button
                  onClick={handleBulkUpload}
                  disabled={bulkUploadLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    padding: '0.625rem 1.5rem',
                    background: bulkUploadLoading ? '#9ca3af' : 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: bulkUploadLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    width: '140px'
                  }}
                >
                  {bulkUploadLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span>Upload & Import</span>
                    </>
                  )}
                </button>
              )}
              
              <button
                onClick={() => {
                  setShowBulkUpload(false);
                  setBulkUploadFile(null);
                  setBulkUploadResults(null);
                  const fileInput = document.getElementById('bulk-upload-file');
                  if (fileInput) fileInput.value = '';
                }}
                style={{
                  padding: '0.625rem 1rem',
                  background: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  width: '140px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Cancel
              </button>
            </div>

            {bulkUploadResults && (
              <div style={{
                padding: '1rem',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginTop: '1rem'
              }}>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                  Upload Results:
                </h4>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                  <span style={{ color: '#10b981' }}>
                    ✅ Valid: {bulkUploadResults.valid_count || 0}
                  </span>
                  <span style={{ color: '#ef4444' }}>
                    ❌ Errors: {bulkUploadResults.error_count || 0}
                  </span>
                  <span style={{ color: '#f59e0b' }}>
                    ⚠️ Duplicates: {bulkUploadResults.duplicate_count || 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Filters */}
        <div style={{ 
          marginBottom: '1.5rem', 
          display: 'flex', 
          gap: '0.75rem', 
          alignItems: 'center',
          flexWrap: 'nowrap'
        }}>
          <input
            type="text"
            placeholder="Search students by name, email, or course..."
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            style={{
              width: '200px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem'
            }}
          />
          
          <select
            value={studentGenderFilter}
            onChange={(e) => setStudentGenderFilter(e.target.value)}
            style={{
              width: '140px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Genders</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          
          <select
            value={studentDeptFilter}
            onChange={(e) => setStudentDeptFilter(e.target.value)}
            style={{
              width: '160px',
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
        
        {/* Students Display - Separated by Gender */}
        {filteredStudents.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faUsers} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b' }}>No students found</p>
          </div>
        ) : (
          <div>
            {renderStudentTable(maleStudents, 'Male Students', '#3b82f6')}
            {renderStudentTable(femaleStudents, 'Female Students', '#ec4899')}
            {renderStudentTable(otherStudents, 'Other Students', '#8b5cf6')}
          </div>
        )}
      </div>
    );
  };

  // Render Alumni Success Stories section
  const renderAlumniStories = () => {
    const handleCreateStory = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          buildApiUrl('/api/moringa/alumni-stories'),
          alumniStoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert('Alumni story created successfully!');
          const storiesResponse = await axios.get(
            buildApiUrl('/api/moringa/alumni-stories'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (storiesResponse.data?.success) {
            setAlumniStories(storiesResponse.data.stories || []);
          }
          setShowAlumniStoryForm(false);
          setAlumniStoryForm({
            alumniName: '', graduationYear: '', department: '', currentPosition: '',
            currentCompany: '', location: '', salaryRange: '', careerTrajectory: [],
            testimonial: '', achievements: [], photoUrl: '', linkedinUrl: '', email: '', isPublished: true
          });
        }
      } catch (error) {
        console.error('Failed to create alumni story:', error);
        alert(error.response?.data?.error || 'Failed to create story.');
      }
    };

    const handleUpdateStory = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          buildApiUrl(`/api/moringa/alumni-stories/${editingAlumniStory.id}`),
          alumniStoryForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success) {
          alert('Story updated!');
          const storiesResponse = await axios.get(buildApiUrl('/api/moringa/alumni-stories'), { headers: { Authorization: `Bearer ${token}` } });
          if (storiesResponse.data?.success) setAlumniStories(storiesResponse.data.stories || []);
          setEditingAlumniStory(null);
          setShowAlumniStoryForm(false);
        }
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to update story.');
      }
    };

    const handleDeleteStory = async (storyId) => {
      if (!window.confirm('Delete this alumni story?')) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(buildApiUrl(`/api/moringa/alumni-stories/${storyId}`), { headers: { Authorization: `Bearer ${token}` } });
        alert('Story deleted!');
        const storiesResponse = await axios.get(buildApiUrl('/api/moringa/alumni-stories'), { headers: { Authorization: `Bearer ${token}` } });
        if (storiesResponse.data?.success) setAlumniStories(storiesResponse.data.stories || []);
      } catch (error) {
        alert('Failed to delete story.');
      }
    };

    const handleEditStory = (story) => {
      setEditingAlumniStory(story);
      setAlumniStoryForm({
        alumniName: story.alumniName || '', graduationYear: story.graduationYear || '', department: story.department || '',
        currentPosition: story.currentPosition || '', currentCompany: story.currentCompany || '', location: story.location || '',
        salaryRange: story.salaryRange || '', careerTrajectory: story.careerTrajectory || [], testimonial: story.testimonial || '',
        achievements: story.achievements || [], photoUrl: story.photoUrl || '', linkedinUrl: story.linkedinUrl || '',
        email: story.email || '', isPublished: story.isPublished !== undefined ? story.isPublished : true
      });
      setShowAlumniStoryForm(true);
    };

    return (
      <div className="section-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2>Alumni Success Stories</h2>
            <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px' }}>
              Showcase alumni achievements, career trajectories, and success stories.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => {
            setEditingAlumniStory(null);
            setAlumniStoryForm({
              alumniName: '', graduationYear: '', department: '', currentPosition: '',
              currentCompany: '', location: '', salaryRange: '', careerTrajectory: [],
              testimonial: '', achievements: [], photoUrl: '', linkedinUrl: '', email: '', isPublished: true
            });
            setShowAlumniStoryForm(true);
          }} style={{ width: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faPlus} />
            <span>Add Story</span>
          </button>
        </div>

        {alumniStoriesLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}><ThemedLoadingSpinner theme="university" /></div>
        ) : alumniStories.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', background: '#f9fafb', borderRadius: '12px', border: '1px dashed #e2e8f0' }}>
            <FontAwesomeIcon icon={faStar} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b' }}>No alumni stories yet</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {alumniStories.map((story) => (
              <div key={story.id} style={{ padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#ffffff' }}>
                {story.photoUrl && (
                  <div style={{ width: '100%', height: '200px', borderRadius: '8px', overflow: 'hidden', background: '#f3f4f6', marginBottom: '1rem' }}>
                    <img src={story.photoUrl} alt={story.alumniName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>{story.alumniName}</h3>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  {story.graduationYear && `Class of ${story.graduationYear}`} {story.department && ` • ${story.department}`}
                </div>
                {story.currentPosition && story.currentCompany && (
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.5rem' }}>
                    {story.currentPosition} at {story.currentCompany}
                  </div>
                )}
                {story.testimonial && (
                  <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: '1.5', marginTop: '0.5rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    "{story.testimonial}"
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => handleEditStory(story)} style={{ flex: 1, fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>Edit</button>
                  <button type="button" className="btn btn-secondary" onClick={() => handleDeleteStory(story.id)} style={{ flex: 1, fontSize: '0.85rem', padding: '0.5rem 0.75rem', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAlumniStoryForm && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}
            onClick={(e) => e.target === e.currentTarget && (setShowAlumniStoryForm(false), setEditingAlumniStory(null))}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
              <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{editingAlumniStory ? 'Edit Story' : 'Add Story'}</h3>
                <button type="button" onClick={() => (setShowAlumniStoryForm(false), setEditingAlumniStory(null))} style={{ background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div><label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Alumni Name *</label>
                      <input type="text" value={alumniStoryForm.alumniName} onChange={(e) => setAlumniStoryForm(prev => ({ ...prev, alumniName: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                    </div>
                    <div><label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Graduation Year *</label>
                      <input type="text" value={alumniStoryForm.graduationYear} onChange={(e) => setAlumniStoryForm(prev => ({ ...prev, graduationYear: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div><label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Current Position *</label>
                      <input type="text" value={alumniStoryForm.currentPosition} onChange={(e) => setAlumniStoryForm(prev => ({ ...prev, currentPosition: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                    </div>
                    <div><label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Current Company *</label>
                      <input type="text" value={alumniStoryForm.currentCompany} onChange={(e) => setAlumniStoryForm(prev => ({ ...prev, currentCompany: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                    </div>
                  </div>
                  <div><label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Testimonial</label>
                    <textarea value={alumniStoryForm.testimonial} onChange={(e) => setAlumniStoryForm(prev => ({ ...prev, testimonial: e.target.value }))} rows="4" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical' }} />
                  </div>
                  <div><label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={alumniStoryForm.isPublished} onChange={(e) => setAlumniStoryForm(prev => ({ ...prev, isPublished: e.target.checked }))} style={{ cursor: 'pointer' }} />
                    <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Publish this story</span>
                  </label></div>
                </div>
              </div>
              <div style={{ padding: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => (setShowAlumniStoryForm(false), setEditingAlumniStory(null))} style={{ width: '140px' }}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={editingAlumniStory ? handleUpdateStory : handleCreateStory} disabled={!alumniStoryForm.alumniName || !alumniStoryForm.graduationYear || !alumniStoryForm.currentPosition || !alumniStoryForm.currentCompany} style={{ width: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faStar} />
                  <span>{editingAlumniStory ? 'Update' : 'Create'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render First Destination Survey section - Part 1 (Complete implementation)
  const renderFirstDestinationSurvey = () => {
    const handleSubmitSurvey = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          buildApiUrl('/api/moringa/first-destination-survey/responses'),
          surveyForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.success) {
          alert('Survey submitted successfully! Thank you for your response.');
          
          // Refresh data
          const [responsesResponse, statsResponse] = await Promise.all([
            axios.get(buildApiUrl('/api/moringa/first-destination-survey/responses'), 
              { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(buildApiUrl('/api/moringa/first-destination-survey/statistics'), 
              { headers: { Authorization: `Bearer ${token}` } })
          ]);
          
          if (responsesResponse.data?.success) {
            setSurveyResponses(responsesResponse.data.responses || []);
          }
          if (statsResponse.data?.success) {
            setSurveyStatistics(statsResponse.data.statistics);
          }
          
          setShowSurveyForm(false);
          setSurveyForm({
            studentName: '', studentEmail: '', graduationYear: '', graduationDate: '',
            department: '', degree: '', destination: '', destinationDetails: '',
            employmentStatus: '', jobTitle: '', companyName: '', industry: '',
            location: '', salaryRange: '', startDate: '', jobSearchDuration: '',
            educationType: '', institutionName: '', programName: '', programStartDate: '',
            businessName: '', businessType: '', businessSector: '',
            isJobRelatedToDegree: null, satisfactionLevel: '', additionalComments: ''
          });
        }
      } catch (error) {
        console.error('Failed to submit survey:', error);
        alert(error.response?.data?.error || 'Failed to submit survey. Please try again.');
      }
    };

    const destinationOptions = [
      { value: 'employed', label: 'Employed' },
      { value: 'further_education', label: 'Pursuing Further Education' },
      { value: 'entrepreneurship', label: 'Entrepreneurship/Business' },
      { value: 'seeking_employment', label: 'Seeking Employment' },
      { value: 'other', label: 'Other' }
    ];

    return (
      <div className="section-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2>First Destination Survey</h2>
            <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px' }}>
              Track where graduates go after graduation - employment, further education, entrepreneurship, or other paths. View aggregated statistics and individual responses.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSurveyStatistics(!showSurveyStatistics)}
              style={{ 
                width: '140px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <FontAwesomeIcon icon={faChartBar} />
              <span>View Statistics</span>
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowSurveyForm(true)}
              style={{ 
                width: '140px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Submit Response</span>
            </button>
          </div>
        </div>

        {/* Statistics View */}
        {showSurveyStatistics && surveyStatistics && (
          <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Survey Statistics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ padding: '1rem', background: 'white', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Responses</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>{surveyStatistics.totalResponses}</div>
              </div>
              {Object.entries(surveyStatistics.destinations || {}).map(([dest, count]) => (
                <div key={dest} style={{ padding: '1rem', background: 'white', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    {dest.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>{count}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Responses List */}
        {surveyLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="university" />
          </div>
        ) : surveyResponses.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faClipboardList} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b' }}>No survey responses yet</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Submit your first response or wait for students to complete the survey.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {surveyResponses.map((response) => (
              <div
                key={response.id}
                style={{
                  padding: '1.25rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                  <div>
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                      {response.studentName || 'Anonymous'}
                    </h4>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                      {response.graduationYear} • {response.department} • {response.degree}
                    </div>
                  </div>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: response.destination === 'employed' ? '#d1fae5' : 
                               response.destination === 'further_education' ? '#dbeafe' :
                               response.destination === 'entrepreneurship' ? '#fef3c7' : '#f3f4f6',
                    color: response.destination === 'employed' ? '#059669' : 
                          response.destination === 'further_education' ? '#2563eb' :
                          response.destination === 'entrepreneurship' ? '#d97706' : '#6b7280'
                  }}>
                    {response.destination ? response.destination.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown'}
                  </span>
                </div>
                {response.destination === 'employed' && response.jobTitle && (
                  <div style={{ fontSize: '0.9rem', color: '#0f172a', marginTop: '0.5rem' }}>
                    {response.jobTitle} at {response.companyName}
                    {response.location && ` • ${response.location}`}
                    {response.salaryRange && ` • ${response.salaryRange}`}
                  </div>
                )}
                {response.submittedAt && (
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                    Submitted: {new Date(response.submittedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Survey Form Modal - Simplified for space, full form can be added */}
        {showSurveyForm && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem'
          }} onClick={(e) => e.target === e.currentTarget && setShowSurveyForm(false)}>
            <div style={{
              backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '700px',
              maxHeight: '90vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>First Destination Survey</h3>
                <button type="button" onClick={() => setShowSurveyForm(false)} style={{ background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Student Name *</label>
                      <input type="text" value={surveyForm.studentName} onChange={(e) => setSurveyForm(prev => ({ ...prev, studentName: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Graduation Year *</label>
                      <input type="text" value={surveyForm.graduationYear} onChange={(e) => setSurveyForm(prev => ({ ...prev, graduationYear: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Current Destination *</label>
                    <select value={surveyForm.destination} onChange={(e) => setSurveyForm(prev => ({ ...prev, destination: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}>
                      <option value="">Select destination...</option>
                      {destinationOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                  {surveyForm.destination === 'employed' && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Job Title</label>
                          <input type="text" value={surveyForm.jobTitle} onChange={(e) => setSurveyForm(prev => ({ ...prev, jobTitle: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Company Name</label>
                          <input type="text" value={surveyForm.companyName} onChange={(e) => setSurveyForm(prev => ({ ...prev, companyName: e.target.value }))} style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Salary Range</label>
                        <input type="text" value={surveyForm.salaryRange} onChange={(e) => setSurveyForm(prev => ({ ...prev, salaryRange: e.target.value }))} placeholder="e.g. KSh 50,000 - 100,000" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem' }} />
                      </div>
                    </>
                  )}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Additional Comments</label>
                    <textarea value={surveyForm.additionalComments} onChange={(e) => setSurveyForm(prev => ({ ...prev, additionalComments: e.target.value }))} rows="3" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div style={{ padding: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowSurveyForm(false)} style={{ width: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSubmitSurvey} disabled={!surveyForm.studentName || !surveyForm.graduationYear || !surveyForm.destination} style={{ width: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faClipboardList} />
                  <span>Submit Survey</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Settings section (similar to University Dashboard)
  const renderSettings = () => (
    <div className="section-card">
      <h2>Moringa Career Services Settings</h2>
      <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
        Configure settings for the Moringa Career Services Platform.
      </p>
      <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
        Settings configuration coming soon...
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="university-dashboard-container">
        <div className="loading-container">
          <ThemedLoadingSpinner theme="university" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Dashboard Header */}
      <UniversityDashboardHeader 
        currentPage={getSectionTitle()}
        onSearch={(term) => console.log('Search:', term)}
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="dashboard-container university-dashboard-container" style={{ marginTop: '0' }}>
        {/* Sidebar */}
        <div 
          className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`} 
          style={{ top: '80px', height: 'calc(100vh - 80px)', paddingTop: '20px' }}
          onClick={(e) => {
            if (window.innerWidth <= 768 && e.target.closest('.nav-item')) {
              setSidebarOpen(false);
            }
          }}
        >
          <div className="nav-menu">
            {navigationItems.map((item) => (
              <div 
                key={item.id}
                className={`nav-item ${activeSection === item.section ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span>{item.label}</span>
                {item.badge && (
                  <span className="badge">
                    {item.badge}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Profile Completion Card */}
          <div className="sidebar-card" style={{ margin: '20px', padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
              Profile Complete
            </div>
            <div style={{ fontSize: '30px', fontWeight: 700, color: '#fff' }}>
              {Math.max(0, Math.min(100, dashboardData.profileCompletion || 0))}%
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="content-area">
            {/* Render sections based on activeSection */}
            {activeSection === 'dashboard' && renderDashboard()}
            {activeSection === 'career-coaching' && renderCareerCoaching()}
            {activeSection === 'job-applications' && renderJobApplications()}
            {activeSection === 'interview-tracking' && renderInterviewTracking()}
            {activeSection === 'aptitude-assessment' && renderAptitudeAssessment()}
            {activeSection === 'graduate-categorisation' && renderGraduateCategorisation()}
            {activeSection === 'mock-interviews' && renderMockInterviews()}
            {activeSection === 'job-boards' && renderJobBoards()}
            {activeSection === 'cv-booklets' && renderCvBooklets()}
            {activeSection === 'employer-events' && renderEmployerEvents()}
            {activeSection === 'alumni-stories' && renderAlumniStories()}
            {activeSection === 'first-destination-survey' && renderFirstDestinationSurvey()}
            {activeSection === 'students' && renderStudents()}
            {activeSection === 'analytics' && renderAnalytics()}
            {activeSection === 'settings' && renderSettings()}
          </div>
        </div>
      </div>
    </>
  );
};

export default MoringaDashboard;

